/**
 * Advanced Trading View Page
 * Enhanced trading interface with modern UI design
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/watchlistSlice';
import { store } from '../redux/store';
import aliceBlueWS from '../services/aliceBlueWebSocket';
import api from '../services/api';
import OrderPanel from '../components/OrderPanel';

const AdvancedTrading = () => {
  const dispatch = useDispatch();
  const watchlist = useSelector((state) => state.watchlist.items);
  const { userId, userSession } = useSelector((state) => state.auth);
  
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Initializing...');
  const [connectionError, setConnectionError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('advancedTrading_activeTab') || 'NSE';
  });
  const [nfoSubTab, setNfoSubTabState] = useState(() => {
    return sessionStorage.getItem('advancedTrading_nfoSubTab') || '';
  });
  const [bfoSubTab, setBfoSubTabState] = useState(() => {
    return sessionStorage.getItem('advancedTrading_bfoSubTab') || '';
  });
  
  // Wrapper to persist nfoSubTab
  const setNfoSubTab = (value) => {
    setNfoSubTabState(value);
    sessionStorage.setItem('advancedTrading_nfoSubTab', value || '');
  };

  // Wrapper to persist bfoSubTab
  const setBfoSubTab = (value) => {
    setBfoSubTabState(value);
    sessionStorage.setItem('advancedTrading_bfoSubTab', value || '');
  };
  
  // Modern UI styling constants
  const uiStyles = {
    primaryGradient: 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600',
    secondaryGradient: 'bg-gradient-to-r from-slate-900 via-gray-900 to-slate-900',
    glassEffect: 'backdrop-blur-lg bg-white/70 border border-white/20 shadow-2xl',
    modernCard: 'bg-white/90 backdrop-blur-xl border border-gray-200/60 shadow-xl rounded-2xl',
    modernTable: 'bg-white/95 backdrop-blur-lg border border-gray-100/60 shadow-2xl rounded-xl',
    modernButton: 'relative overflow-hidden transition-all duration-300 transform hover:scale-105',
    textGradient: 'bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'
  };
  
  const [nfoSymbolsOrder, setNfoSymbolsOrder] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_nfoSymbolsOrder');
    return saved ? JSON.parse(saved) : [];
  }); // Custom order for NFO symbols
  const [bfoSymbolsOrder, setBfoSymbolsOrder] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_bfoSymbolsOrder');
    return saved ? JSON.parse(saved) : [];
  }); // Custom order for BFO symbols
  const [draggedSymbolId, setDraggedSymbolId] = useState(null);
  
  // Master symbols state
  const [masterSymbolsByExchange, setMasterSymbolsByExchange] = useState({});
  const [selectedMasterSymbol, setSelectedMasterSymbol] = useState({});
  
  // Order form state
  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [orderData, setOrderData] = useState({
    symbol: null,
    action: 'BUY',
    quantity: '',
    price: '',
    orderType: 'LIMIT',
    validity: 'DAY',
    product: 'MIS'
  });

  // Order Panel state
  const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // Exchange-specific search states
  const [exchangeSearchQueries, setExchangeSearchQueries] = useState({});
  const [exchangeSearchResults, setExchangeSearchResults] = useState({});
  
  // Symbol filter state for options trading
  const [symbolFilters, setSymbolFilters] = useState({
    symbolName: '',
    expiryDate: '',
    optionType: '',
    strikePrice: '',
    exchange: 'NFO'
  });
  const [filterResults, setFilterResults] = useState({ symbols: [], count: 0 });
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [availableStrikes, setAvailableStrikes] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState(['NIFTY', 'BANKNIFTY', 'FINNIFTY']);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [editableBaseStrike, setEditableBaseStrike] = useState('');
  
  // Order entry states with localStorage persistence
  const [selectedCEStrike, setSelectedCEStrikeState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_selectedCEStrike');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedPEStrike, setSelectedPEStrikeState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_selectedPEStrike');
    return saved ? JSON.parse(saved) : null;
  });
  const [orderType, setOrderType] = useState(() => {
    return localStorage.getItem('advancedTrading_orderType') || 'LIMIT';
  });
  const [productType, setProductType] = useState(() => {
    return localStorage.getItem('advancedTrading_productType') || 'NRML';
  });
  const [ceQty, setCEQtyState] = useState(() => {
    return localStorage.getItem('advancedTrading_ceQty') || '';
  });
  const [peQty, setPEQtyState] = useState(() => {
    return localStorage.getItem('advancedTrading_peQty') || '';
  });
  const [positions, setPositions] = useState([]);
  const [depthData, setDepthData] = useState({}); // Depth data for next expiry
  
  // Collapse state for options tables section
  const [isOptionsTableCollapsed, setIsOptionsTableCollapsed] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_optionsTableCollapsed');
    return saved === 'true';
  });
  
  // Collapse state for exchange tabs section
  const [isExchangeTabsCollapsed, setIsExchangeTabsCollapsed] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_exchangeTabsCollapsed');
    return saved === 'true';
  });
  
  // Bull vs Bear export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [lastExportSessionId, setLastExportSessionId] = useState(null);
  
  // Flip table state
  const [isFlipped, setIsFlipped] = useState(true); // Default to flipped (difference) view
  const [exportedData, setExportedData] = useState([]);
  const [isFetchingExported, setIsFetchingExported] = useState(false);
  
  // Popup notification state
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('success'); // 'success' or 'error'
  
  // Direction tracking and notification states
  const [ceDirection, setCEDirection] = useState(null); // 'up', 'down', or null
  const [peDirection, setPEDirection] = useState(null); // 'up', 'down', or null
  const [showDirectionNotification, setShowDirectionNotification] = useState(false);
  const [directionNotificationData, setDirectionNotificationData] = useState(null);
  const [disableNotificationsUntil, setDisableNotificationsUntil] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_disableNotificationsUntil');
    return saved ? parseInt(saved) : null;
  });
  const [notificationsDismissed, setNotificationsDismissed] = useState({});
  
  // MTM tracking - baseline LTP sums from when "Analyse Options Chain" was clicked
  // Persisted in localStorage and also saved to database for cross-device support
  const [baselineCESum, setBaselineCESumState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_baselineCESum');
    return saved ? parseFloat(saved) : 0;
  });
  const [baselinePESum, setBaselinePESumState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_baselinePESum');
    return saved ? parseFloat(saved) : 0;
  });
  
  const [nextExpiryDate, setNextExpiryDateState] = useState(() => {
    // Restore from localStorage on mount
    const saved = localStorage.getItem('advancedTrading_nextExpiryDate');
    return saved || null;
  });
  const [nextExpirySymbols, setNextExpirySymbolsState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_nextExpirySymbols');
    return saved ? JSON.parse(saved) : [];
  });

  // Restore selectedChainInfo and related states from localStorage (persists across logout/login)
  const [selectedChainInfo, setSelectedChainInfoState] = useState(() => {
    const saved = localStorage.getItem('advancedTrading_selectedChainInfo');
    return saved ? JSON.parse(saved) : null;
  });

  // Wrapper to persist selectedChainInfo
  const setSelectedChainInfo = (value) => {
    setSelectedChainInfoState(value);
    if (value) {
      localStorage.setItem('advancedTrading_selectedChainInfo', JSON.stringify(value));
    } else {
      localStorage.removeItem('advancedTrading_selectedChainInfo');
    }
  };

  // Persist activeTab when it changes
  useEffect(() => {
    sessionStorage.setItem('advancedTrading_activeTab', activeTab);
  }, [activeTab]);
  
  // Cleanup expired notification disable timer
  useEffect(() => {
    if (disableNotificationsUntil) {
      const now = Date.now();
      if (now >= disableNotificationsUntil) {
        setDisableNotificationsUntil(null);
        localStorage.removeItem('advancedTrading_disableNotificationsUntil');
      }
    }
  }, [disableNotificationsUntil]);

  // Wrapper functions for Quick Order Entry state persistence
  const setSelectedCEStrike = (value) => {
    setSelectedCEStrikeState(value);
    if (value) {
      localStorage.setItem('advancedTrading_selectedCEStrike', JSON.stringify(value));
      // Ensure quantity is at least lot size when selecting a symbol
      const lotSize = value.lotSize || 25;
      const currentQty = parseInt(ceQty) || 0;
      if (currentQty < lotSize) {
        setCEQty(lotSize.toString());
      }
    } else {
      localStorage.removeItem('advancedTrading_selectedCEStrike');
    }
  };

  const setSelectedPEStrike = (value) => {
    setSelectedPEStrikeState(value);
    if (value) {
      localStorage.setItem('advancedTrading_selectedPEStrike', JSON.stringify(value));
      // Ensure quantity is at least lot size when selecting a symbol
      const lotSize = value.lotSize || 25;
      const currentQty = parseInt(peQty) || 0;
      if (currentQty < lotSize) {
        setPEQty(lotSize.toString());
      }
    } else {
      localStorage.removeItem('advancedTrading_selectedPEStrike');
    }
  };

  const setCEQty = (value) => {
    // Validate quantity against lot size if CE strike is selected
    if (selectedCEStrike && value) {
      const lotSize = selectedCEStrike.lotSize || 25;
      const qtyNum = parseInt(value);
      if (!isNaN(qtyNum) && qtyNum < lotSize) {
        value = lotSize.toString();
      }
    }
    setCEQtyState(value);
    localStorage.setItem('advancedTrading_ceQty', value);
  };

  const setPEQty = (value) => {
    // Validate quantity against lot size if PE strike is selected
    if (selectedPEStrike && value) {
      const lotSize = selectedPEStrike.lotSize || 25;
      const qtyNum = parseInt(value);
      if (!isNaN(qtyNum) && qtyNum < lotSize) {
        value = lotSize.toString();
      }
    }
    setPEQtyState(value);
    localStorage.setItem('advancedTrading_peQty', value);
  };

  // Persist orderType changes
  useEffect(() => {
    localStorage.setItem('advancedTrading_orderType', orderType);
  }, [orderType]);

  // Persist productType changes
  useEffect(() => {
    localStorage.setItem('advancedTrading_productType', productType);
  }, [productType]);

  // Wrapper functions for MTM baseline state with localStorage persistence
  const setBaselineCESum = (value) => {
    setBaselineCESumState(value);
    if (value && value > 0) {
      localStorage.setItem('advancedTrading_baselineCESum', value.toString());
    } else {
      localStorage.removeItem('advancedTrading_baselineCESum');
    }
  };

  const setBaselinePESum = (value) => {
    setBaselinePESumState(value);
    if (value && value > 0) {
      localStorage.setItem('advancedTrading_baselinePESum', value.toString());
    } else {
      localStorage.removeItem('advancedTrading_baselinePESum');
    }
  };

  // Wrapper functions for Next Expiry state persistence
  const setNextExpiryDate = (value) => {
    setNextExpiryDateState(value);
    if (value) {
      localStorage.setItem('advancedTrading_nextExpiryDate', value);
    } else {
      localStorage.removeItem('advancedTrading_nextExpiryDate');
    }
  };

  // Quantity adjustment functions
  const adjustQuantity = (optionType, operation) => {
    const currentStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    const currentQty = optionType === 'CE' ? ceQty : peQty;
    
    if (!currentStrike) return;
    
    const lotSize = currentStrike.lotSize || 25; // Default to 25 if not available
    const currentQtyNum = parseInt(currentQty) || lotSize;
    
    let newQty;
    if (operation === 'increase') {
      newQty = currentQtyNum + lotSize;
    } else if (operation === 'decrease') {
      newQty = Math.max(lotSize, currentQtyNum - lotSize); // Don't go below lot size
    }
    
    if (optionType === 'CE') {
      setCEQty(newQty.toString());
    } else {
      setPEQty(newQty.toString());
    }
  };

  // Check if decrease button should be disabled
  const isDecreaseDisabled = (optionType) => {
    const currentStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    const currentQty = optionType === 'CE' ? ceQty : peQty;
    
    if (!currentStrike) return true;
    
    const lotSize = currentStrike.lotSize || 25;
    const currentQtyNum = parseInt(currentQty) || lotSize;
    
    return currentQtyNum <= lotSize;
  };

  // Handle Re Enter button - places same order again
  const handleReEnter = (optionType) => {
    const strikeData = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    if (strikeData) {
      // Get the last order direction or default to BUY
      const lastDirection = localStorage.getItem(`lastDirection_${optionType}`) || 'BUY';
      handleQuickOrder(strikeData, lastDirection, optionType);
    }
  };

  // Get current direction for display
  const getCurrentDirection = (optionType) => {
    return localStorage.getItem(`lastDirection_${optionType}`) || 'BUY';
  };

  // Handle Exit position
  const handleQuickExit = (optionType) => {
    const strikeData = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    if (strikeData) {
      const lastDirection = localStorage.getItem(`lastDirection_${optionType}`);
      // Exit is opposite of last direction
      const exitDirection = lastDirection === 'BUY' ? 'SELL' : 'BUY';
      handleQuickOrder(strikeData, exitDirection, optionType);
    }
  };

  const setNextExpirySymbols = (value) => {
    setNextExpirySymbolsState(value);
    if (value && value.length > 0) {
      localStorage.setItem('advancedTrading_nextExpirySymbols', JSON.stringify(value));
    } else {
      localStorage.removeItem('advancedTrading_nextExpirySymbols');
    }
  };

  // Re-subscribe to depth data when component mounts with saved state
  useEffect(() => {
    if (wsConnected && nextExpirySymbols.length > 0) {
      console.log('🔄 Re-subscribing to saved depth data:', nextExpirySymbols.length, 'symbols');
      console.log('📊 Next expiry symbols:', nextExpirySymbols);
      aliceBlueWS.subscribeDepth(nextExpirySymbols);
      
      // Log what we're subscribing to
      nextExpirySymbols.forEach(sym => {
        console.log(`  📡 Subscribing depth: ${sym.exchange}|${sym.token} - ${sym.tradingSymbol} (${sym.type} ${sym.strike})`);
      });
    } else {
      console.log('⚠️ Not subscribing to depth data:', { wsConnected, symbolsCount: nextExpirySymbols.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected]); // Only run when wsConnected changes (on mount and reconnect)

  useEffect(() => {
    // Fetch master symbols when component mounts
    const fetchMasterSymbols = async () => {
      try {
        const response = await api.get('/master-symbols/by-exchange', {
          headers: { Authorization: `Bearer ${userSession}` }
        });

        if (response.data.success) {
          setMasterSymbolsByExchange(response.data.data || {});
          // Initialize NFO symbols order
          if (response.data.data?.NFO) {
            setNfoSymbolsOrder(response.data.data.NFO.map(s => s.id));
          }
        } else {
          console.error('Failed to fetch master symbols:', response.data.error);
        }
      } catch (error) {
        console.error('Error fetching master symbols:', error);
      }
    };

    // Fetch baseline sums from database for MTM calculation
    // Only if localStorage doesn't have them (for cross-device sync)
    const fetchBaselineSums = async () => {
      // Check if localStorage already has baseline values
      const localCESum = localStorage.getItem('advancedTrading_baselineCESum');
      const localPESum = localStorage.getItem('advancedTrading_baselinePESum');
      
      // If localStorage has values, use those (they're already loaded in state)
      if (localCESum && localPESum) {
        console.log('✅ Using baseline sums from localStorage:', { CE: localCESum, PE: localPESum });
        return;
      }
      
      // Otherwise, try to fetch from database
      try {
        const response = await api.get('/options-analysis/baseline-sums', {
          headers: { Authorization: `Bearer ${userSession}` }
        });

        if (response.data.success && response.data.data.hasBaseline) {
          console.log('📊 Loaded baseline sums from database:', response.data.data);
          // Use wrapper functions to persist to localStorage
          setBaselineCESum(response.data.data.baselineCESum);
          setBaselinePESum(response.data.data.baselinePESum);
        } else {
          console.log('ℹ️ No baseline data found in database or localStorage');
        }
      } catch (error) {
        console.error('Error fetching baseline sums:', error);
      }
    };

    if (userSession) {
      fetchMasterSymbols();
      fetchBaselineSums();
    }
  }, [userSession]);

  // Auto-select first NFO sub-tab when master symbols are loaded and NFO is active
  // Auto-select first sub-tab when master symbols are loaded
  useEffect(() => {
    // Auto-select first NFO sub-tab
    if (activeTab === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0 && !nfoSubTab) {
      const firstSymbol = masterSymbolsByExchange['NFO'][0].symbol_name;
      setNfoSubTab(firstSymbol);
      handleSymbolNameChange(firstSymbol, 'NFO');
    }
    // Auto-select first BFO sub-tab
    if (activeTab === 'BFO' && masterSymbolsByExchange['BFO']?.length > 0 && !bfoSubTab) {
      const firstSymbol = masterSymbolsByExchange['BFO'][0].symbol_name;
      setBfoSubTab(firstSymbol);
      handleSymbolNameChange(firstSymbol, 'BFO');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, masterSymbolsByExchange]);

  // Fetch expiries when sub-tab changes
  useEffect(() => {
    const fetchExpiriesForSubTab = async () => {
      const currentSymbol = activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : '';
      const currentExchange = activeTab;
      
      if ((activeTab === 'NFO' || activeTab === 'BFO') && currentSymbol) {
        try {
          const response = await api.get(
            `/symbol-filters/expiry-dates?symbolName=${currentSymbol}&exchange=${currentExchange}`,
            { headers: { Authorization: `Bearer ${userSession}` } }
          );

          if (response.data.success) {
            setAvailableExpiries(response.data.data);
            // Update symbolFilters to include the symbol name from sub-tab
            setSymbolFilters(prev => ({
              ...prev,
              symbolName: currentSymbol
            }));
          }
        } catch (error) {
          console.error('Error fetching expiry dates for sub-tab:', error);
        }
      }
    };

    fetchExpiriesForSubTab();
  }, [activeTab, nfoSubTab, bfoSubTab, userSession]);

  // Drag and drop handlers for NFO sub-tabs
  const handleDragStart = (e, symbolId) => {
    setDraggedSymbolId(symbolId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSymbolId) => {
    e.preventDefault();
    
    if (draggedSymbolId === targetSymbolId) return;

    const newOrder = [...nfoSymbolsOrder];
    const draggedIndex = newOrder.indexOf(draggedSymbolId);
    const targetIndex = newOrder.indexOf(targetSymbolId);

    // Remove dragged item and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedSymbolId);

    setNfoSymbolsOrder(newOrder);
    setDraggedSymbolId(null);
  };

  const handleDragEnd = () => {
    setDraggedSymbolId(null);
  };

  useEffect(() => {
    // Connect to AliceBlue WebSocket on mount (automatically reconnect when page loads)
    setWsStatus('Connecting to WebSocket...');
    setConnectionError(null);
    
    const connectWebSocket = async () => {
      try {
        // Always attempt to connect/reconnect when page loads
        console.log('🔌 Auto-connecting WebSocket on page load...');
        
        // Disconnect first if already connected to ensure fresh connection
        if (aliceBlueWS.isConnected) {
          console.log('🔄 Disconnecting existing connection before reconnecting...');
          aliceBlueWS.disconnect();
        }
        
        await aliceBlueWS.connect();
        
        if (aliceBlueWS.isConnected) {
          setWsStatus('Connected');
          setConnectionError(null);
          setWsConnected(true);
          console.log('✅ WebSocket connected successfully (auto-connect)');
        }
      } catch (err) {
        console.error('❌ Failed to auto-connect WebSocket:', err);
        
        let errorMessage = 'WebSocket connection failed';
        
        // Parse different error types
        if (err.message.includes('No session ID in response')) {
          errorMessage = 'AliceBlue API authentication failed - Please check API credentials';
        } else if (err.message.includes('HTTP 500')) {
          errorMessage = 'Server error - Please check backend configuration';
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Backend server unavailable - Please check if server is running';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Connection timeout - Working in offline mode';
        } else {
          errorMessage = err.message + ' - Working in offline mode';
        }
        
        setConnectionError(errorMessage);
        setWsStatus('Offline Mode');
        setWsConnected(false);
        
        // Show user-friendly notification instead of just console error
        console.log('🔄 You can still use the application without real-time data');
      }
    };

    connectWebSocket();

    // Subscribe to connection status
    const connectionCallback = (connected) => {
      setWsConnected(connected);
      setWsStatus(connected ? 'Connected' : 'Disconnected');
      if (connected) {
        setConnectionError(null);
        console.log('✅ WebSocket connected, re-subscribing to watchlist...');
        if (watchlist.length > 0) {
          // Re-subscribe to watchlist when reconnected
          const symbols = watchlist.map(item => ({
            exchange: item.exchange,
            token: item.instrumentId
          }));
          console.log('📡 Re-subscribing to', symbols.length, 'symbols');
          aliceBlueWS.subscribe(symbols);
        }
      }
    };
    aliceBlueWS.addSubscriber('_connection', connectionCallback);

    // Subscribe to all price updates
    const priceCallback = (data) => {
      setLivePrices(prev => {
        const key = `${data.exchange}|${data.token}`;
        
        // Handle depth data separately
        if (data.type === 'depth') {
          setDepthData(prevDepth => {
            const existing = prevDepth[key] || {};
            // Merge new data with existing, preserving previous values if new ones are undefined
            const updated = {
              ...prevDepth,
              [key]: {
                tbq: data.tbq !== undefined ? data.tbq : existing.tbq,
                tsq: data.tsq !== undefined ? data.tsq : existing.tsq,
                lp: data.lp !== undefined ? data.lp : existing.lp
              }
            };
            
            // Log depth data updates
            if (data.tbq !== undefined || data.tsq !== undefined) {
              console.log(`📊 Depth data stored for ${key}:`, updated[key]);
            }
            
            return updated;
          });
          // Also update live prices for LTP
          if (data.lp) {
            return {
              ...prev,
              [key]: {
                ...prev[key],
                ltp: data.lp
              }
            };
          }
          return prev;
        }
        
        // Merge new data with existing data to preserve previous values
        return {
          ...prev,
          [key]: {
            ...prev[key], // Keep previous values
            ...data,      // Update with new values
            // Ensure LTP is never set to 0 or null if we have a previous value
            ltp: (data.lp && data.lp > 0) ? data.lp : (prev[key]?.ltp || data.lp),
            // Ensure percentage change is preserved if not in new data
            pc: (data.pc !== undefined && data.pc !== null) ? data.pc : (prev[key]?.pc),
            // Preserve high and low values - only update if new value exists, keep track of day's high/low
            h: (data.h !== undefined && data.h !== null) ? data.h : prev[key]?.h,
            l: (data.l !== undefined && data.l !== null) ? data.l : prev[key]?.l,
            // Log when we receive h/l data
            _hasHighLow: (data.h !== undefined || data.l !== undefined) || prev[key]?._hasHighLow
          }
        };
      });
    };
    aliceBlueWS.addSubscriber('*', priceCallback);

    return () => {
      aliceBlueWS.removeSubscriber('_connection', connectionCallback);
      aliceBlueWS.removeSubscriber('*', priceCallback);
    };
  }, [watchlist]);

  useEffect(() => {
    // Subscribe to watchlist symbols when watchlist changes or when WS connects
    if (wsConnected && watchlist.length > 0) {
      console.log('📡 Subscribing to watchlist symbols:', watchlist.length, 'items');
      const symbols = watchlist.map(item => ({
        exchange: item.exchange,
        token: item.instrumentId
      }));
      aliceBlueWS.subscribe(symbols);
      
      // Also subscribe to depth data for NFO/BFO options to get TBQ/TSQ
      const depthSymbols = watchlist
        .filter(item => item.exchange === 'NFO' || item.exchange === 'BFO')
        .map(item => ({
          exchange: item.exchange,
          token: item.instrumentId,
          tradingSymbol: item.tradingSymbol
        }));
      
      if (depthSymbols.length > 0) {
        console.log('📊 Subscribing to depth data for options:', depthSymbols.length, 'symbols');
        aliceBlueWS.subscribeDepth(depthSymbols);
      }
    }
  }, [watchlist, wsConnected]);

  // Auto-populate header info when NFO/BFO options are in watchlist
  useEffect(() => {
    if ((activeTab === 'NFO' || activeTab === 'BFO') && !selectedChainInfo && watchlist.length > 0) {
      const options = watchlist.filter(item => item.exchange === activeTab && item.tradingSymbol);
      if (options.length > 0) {
        // Extract info from first option
        const firstOption = options[0];
        if (!firstOption.tradingSymbol) return;
        
        const symbolMatch = firstOption.tradingSymbol.match(/^([A-Z]+)/);
        // NFO: 02DEC25, BFO: 25DEC
        const expiryMatch = firstOption.tradingSymbol.match(/(\d{2}[A-Z]{3}\d{0,2})/);
        // NFO: C26000, BFO: 72800CE
        const strikeMatch = firstOption.tradingSymbol.match(/[CP](\d+)$/) || firstOption.tradingSymbol.match(/(\d{3,5})[CP]E$/);
        
        if (symbolMatch && expiryMatch && strikeMatch) {
          // Find all strikes to determine the base/center strike
          const allStrikes = options
            .filter(opt => opt.tradingSymbol)
            .map(opt => {
              let match = opt.tradingSymbol.match(/[CP](\d+)$/);
              if (!match) {
                match = opt.tradingSymbol.match(/(\d{3,5})[CP]E$/);
              }
              return match ? parseInt(match[1]) : null;
            })
            .filter(s => s !== null)
            .sort((a, b) => a - b);
          
          // Use middle strike as base strike
          const middleIndex = Math.floor(allStrikes.length / 2);
          const baseStrike = allStrikes[middleIndex] || parseInt(strikeMatch[1]);
          
          setSelectedChainInfo({
            symbolName: symbolMatch[1],
            expiryDate: expiryMatch[1],
            centerStrike: baseStrike.toLocaleString()
          });
        }
      }
    }
  }, [activeTab, watchlist, selectedChainInfo]);

  // Fetch and subscribe to next expiry depth data
  useEffect(() => {
    const fetchNextExpiryData = async () => {
      // Get current subtab based on active tab
      const currentSubTab = activeTab === 'NFO' ? nfoSubTab : bfoSubTab;

      // Don't fetch if we're currently building the options chain
      if (window.isBuildingOptionsChain) {
        console.log('🚫 Skipping next expiry fetch - building options chain');
        return;
      }
      
      // Clear previous data if chain has changed
      if (selectedChainInfo && selectedChainInfo.expiryDateRaw) {
        const currentExpiry = selectedChainInfo.expiryDateRaw || selectedChainInfo.expiryDate;
        console.log('🔄 Current chain expiry:', currentExpiry);
      }
      
      if ((activeTab !== 'NFO' && activeTab !== 'BFO') || !selectedChainInfo || !currentSubTab) {
        console.log('⏭️ Skipping next expiry fetch:', { activeTab, selectedChainInfo: !!selectedChainInfo, currentSubTab });
        return;
      }

      try {
        console.log('🔍 Fetching next expiry for:', currentSubTab);
        console.log('🔍 Selected chain info:', selectedChainInfo);
        
        // Fetch all available expiry dates for the symbol
        const response = await api.get(
          `/symbol-filters/expiry-dates?symbolName=${currentSubTab}&exchange=${activeTab}`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        );

        console.log('📅 Expiry dates response:', response.data);

        if (response.data.success && response.data.data.length > 1) {
          const expiries = response.data.data;
          const currentExpiry = selectedChainInfo.expiryDateRaw || selectedChainInfo.expiryDate; // Use raw date if available
          
          console.log('📅 All expiries:', expiries.map(e => e.date));
          console.log('📅 Looking for current expiry:', currentExpiry);
          
          // Find next expiry after current
          const currentIndex = expiries.findIndex(e => e.date === currentExpiry);
          const nextExpiry = currentIndex >= 0 && currentIndex < expiries.length - 1
            ? expiries[currentIndex + 1]
            : null;

          console.log('📆 Current expiry:', currentExpiry, 'Index:', currentIndex, 'Next:', nextExpiry);

          if (nextExpiry) {
            setNextExpiryDate(nextExpiry.formatted);
            console.log('✅ Set next expiry date to:', nextExpiry.formatted);

            // Get all strikes from current watchlist
            const currentExchange = activeTab;
            const currentSubTab = activeTab === 'NFO' ? nfoSubTab : bfoSubTab;
            
            const optionsFromWatchlist = watchlist.filter(item =>
              item.exchange === currentExchange &&
              item.tradingSymbol.startsWith(currentSubTab)
            );

            const strikes = [...new Set(
              optionsFromWatchlist.map(opt => {
                // Use strikePrice if available from watchlist item
                if (opt.strikePrice) return opt.strikePrice.toString();
                
                // Fallback to parsing trading symbol
                // NFO: NIFTY25DEC25C26000 -> 26000
                let match = opt.tradingSymbol.match(/[CP](\d+)$/);
                if (match) return match[1];
                
                // BFO Weekly: SENSEX2610174900CE -> 74900
                if (opt.tradingSymbol.startsWith('SENSEX')) {
                  match = opt.tradingSymbol.match(/^SENSEX\d{2}[1-9OND]\d{2}(\d+)[CP]E$/);
                  if (match) return match[1];
                }
                
                // BFO Standard: SENSEX25DEC72800CE -> 72800
                match = opt.tradingSymbol.match(/(\d{3,5})[CP]E$/);
                return match ? match[1] : null;
              }).filter(Boolean)
            )];

            console.log(`🎯 Strikes to fetch for ${currentExchange} ${currentSubTab}:`, strikes);

            // Fetch next expiry symbols for depth subscription
            const nextExpirySymbols = [];
            for (const strike of strikes) {
              try {
                // Fetch CE option
                const ceResponse = await api.get(
                  `/symbol-filters/search?symbolName=${currentSubTab}&expiryDate=${nextExpiry.date}&optionType=CE&strikePrice=${strike}&exchange=${currentExchange}`,
                  { headers: { Authorization: `Bearer ${userSession}` } }
                );
                if (ceResponse.data.success && ceResponse.data.data.length > 0) {
                  const ceData = ceResponse.data.data[0];
                  nextExpirySymbols.push({
                    exchange: currentExchange,
                    token: ceData.instrument_id, // Use instrument_id from backend
                    strike: strike,
                    type: 'CE',
                    tradingSymbol: ceData.trading_symbol // Use trading_symbol from backend
                  });
                  console.log(`✅ CE ${strike}: token=${ceData.instrument_id}, symbol=${ceData.trading_symbol}`);
                }

                // Fetch PE option
                const peResponse = await api.get(
                  `/symbol-filters/search?symbolName=${currentSubTab}&expiryDate=${nextExpiry.date}&optionType=PE&strikePrice=${strike}&exchange=${currentExchange}`,
                  { headers: { Authorization: `Bearer ${userSession}` } }
                );
                if (peResponse.data.success && peResponse.data.data.length > 0) {
                  const peData = peResponse.data.data[0];
                  nextExpirySymbols.push({
                    exchange: currentExchange,
                    token: peData.instrument_id, // Use instrument_id from backend
                    strike: strike,
                    type: 'PE',
                    tradingSymbol: peData.trading_symbol // Use trading_symbol from backend
                  });
                  console.log(`✅ PE ${strike}: token=${peData.instrument_id}, symbol=${peData.trading_symbol}`);
                }
              } catch (error) {
                console.warn(`Failed to fetch next expiry for strike ${strike}:`, error);
              }
            }

            // Subscribe to depth data for next expiry symbols
            if (nextExpirySymbols.length > 0 && wsConnected) {
              console.log('📊 Subscribing to next expiry depth data:', nextExpirySymbols.length, 'symbols');
              setNextExpirySymbols(nextExpirySymbols);
              aliceBlueWS.subscribeDepth(nextExpirySymbols);
            } else {
              console.warn('⚠️ No symbols to subscribe or WS not connected:', {
                symbolsCount: nextExpirySymbols.length,
                wsConnected
              });
            }
          } else {
            console.warn('⚠️ No next expiry found');
            setNextExpiryDate(null);
          }
        } else {
          console.warn('⚠️ Not enough expiry dates or request failed');
        }
      } catch (error) {
        console.error('❌ Error fetching next expiry data:', error);
      }
    };

    fetchNextExpiryData();
  }, [activeTab, selectedChainInfo?.expiryDateRaw, nfoSubTab, wsConnected, userSession]);
  // Removed nextExpiryDate and nextExpirySymbols.length from dependencies to allow re-fetching

  // Auto-fetch exported data when in flipped mode and next expiry is available
  useEffect(() => {
    if (isFlipped && nextExpiryDate && selectedChainInfo && exportedData.length === 0) {
      console.log('🔄 Auto-fetching exported data for difference view');
      handleFetchExportedData();
    }
  }, [nextExpiryDate, isFlipped, selectedChainInfo]);

  // Export Bull vs Bear data to database
  // Fetch exported data from SQL database
  const handleFetchExportedData = async () => {
    if (!selectedChainInfo || !nextExpiryDate) {
      return;
    }
    
    setIsFetchingExported(true);
    
    try {
      // Convert nextExpiryDate from "09DEC25" format to "YYYY-MM-DD"
      const convertDateFormat = (dateStr) => {
        if (!dateStr) return null;
        const match = dateStr.match(/(\d{2})([A-Z]{3})(\d{2})/);
        if (!match) return null;
        const day = match[1];
        const month = match[2];
        const year = '20' + match[3];
        const monthMap = {
          'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
          'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
          'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
        };
        return `${year}-${monthMap[month]}-${day}`;
      };
      
      const formattedExpiryDate = convertDateFormat(nextExpiryDate);
      
      if (!formattedExpiryDate) {
        console.error('Invalid expiry date format');
        setIsFetchingExported(false);
        return;
      }
      
      const response = await fetch(
        `/api/bull-bear/latest?symbolName=${encodeURIComponent(selectedChainInfo.symbolName)}&expiryDate=${encodeURIComponent(formattedExpiryDate)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch exported data');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setExportedData(result.data);
        console.log(`✅ Fetched ${result.data.length} exported records from SQL`);
      } else {
        setExportedData([]);
        console.warn('No exported data found in database');
      }
    } catch (error) {
      console.error('Error fetching exported data:', error);
      setExportedData([]);
    } finally {
      setIsFetchingExported(false);
    }
  };
  
  const handleExportBullBear = async () => {
    if (!nextExpiryDate || !selectedChainInfo || ceOptions.length === 0) {
      setExportMessage('❌ No data to export. Please analyse the option chain first.');
      setTimeout(() => setExportMessage(''), 3000);
      return;
    }

    setIsExporting(true);
    setExportMessage('');

    try {
      // Convert nextExpiryDate from "09DEC25" or "24DEC" format to "YYYY-MM-DD"
      const convertDateFormat = (dateStr) => {
        if (!dateStr) return null;
        
        // Expected format: "09DEC25" (NFO)
        let match = dateStr.match(/(\d{2})([A-Z]{3})(\d{2})/);
        if (match) {
          const day = match[1];
          const month = match[2];
          const year = '20' + match[3];
          
          const monthMap = {
            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
          };
          
          return `${year}-${monthMap[month]}-${day}`;
        }

        // Expected format: "24DEC" (BFO)
        match = dateStr.match(/(\d{2})([A-Z]{3})/);
        if (match) {
          const day = match[1];
          const monthStr = match[2];
          const year = new Date().getFullYear();
          
          const monthMap = {
            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
          };
          
          return `${year}-${monthMap[monthStr]}-${day}`;
        }
        
        return null;
      };
      
      const formattedExpiryDate = convertDateFormat(nextExpiryDate);
      
      if (!formattedExpiryDate) {
        setExportMessage('❌ Invalid expiry date format');
        setTimeout(() => setExportMessage(''), 3000);
        setIsExporting(false);
        return;
      }
      
      const exportData = [];
      
      // Process by iterating through PE options (descending order)
      peOptions.forEach((peItem, idx) => {
        const ceItem = ceOptions[idx];
        
        // Extract strike prices - support both NFO and BFO formats
        const extractStrike = (symbol) => {
          if (!symbol) return null;
          let match = symbol.match(/[CP](\d+)$/); // NFO
          if (match) return match[1];
          match = symbol.match(/(\d{3,5})[CP]E$/); // BFO
          return match ? match[1] : null;
        };

        const peStrike = extractStrike(peItem.tradingSymbol);
        const ceStrike = ceItem ? extractStrike(ceItem.tradingSymbol) : null;
        
        // Process PE strike data
        if (peStrike) {
          const nextPE = nextExpirySymbols.find(s => s.strike === peStrike && s.type === 'PE');
          const peKey = nextPE ? `${nextPE.exchange}|${nextPE.token}` : null;
          const peTBQ = (peKey && depthData[peKey]?.tbq) || 0;
          const peTSQ = (peKey && depthData[peKey]?.tsq) || 0;
          
          // Check if this strike already exists in exportData
          let existingEntry = exportData.find(item => item.strikePrice === parseInt(peStrike));
          
          if (existingEntry) {
            // Update PE data in existing entry
            existingEntry.pe = nextPE ? {
              tradingSymbol: nextPE.tradingSymbol || null,
              exchange: nextPE.exchange || null,
              instrumentId: nextPE.token || null,
              tbq: typeof peTBQ === 'number' ? peTBQ : 0,
              tsq: typeof peTSQ === 'number' ? peTSQ : 0
            } : null;
          } else if (nextPE) {
            // Create new entry with PE data
            exportData.push({
              symbolName: selectedChainInfo.symbolName,
              expiryDate: nextExpiryDate,
              strikePrice: parseInt(peStrike),
              ce: null,
              pe: {
                tradingSymbol: nextPE.tradingSymbol || null,
                exchange: nextPE.exchange || null,
                instrumentId: nextPE.token || null,
                tbq: typeof peTBQ === 'number' ? peTBQ : 0,
                tsq: typeof peTSQ === 'number' ? peTSQ : 0
              }
            });
          }
        }
        
        // Process CE strike data
        if (ceStrike) {
          const nextCE = nextExpirySymbols.find(s => s.strike === ceStrike && s.type === 'CE');
          const ceKey = nextCE ? `${nextCE.exchange}|${nextCE.token}` : null;
          const ceTBQ = (ceKey && depthData[ceKey]?.tbq) || 0;
          const ceTSQ = (ceKey && depthData[ceKey]?.tsq) || 0;
          
          // Check if this strike already exists in exportData
          let existingEntry = exportData.find(item => item.strikePrice === parseInt(ceStrike));
          
          if (existingEntry) {
            // Update CE data in existing entry
            existingEntry.ce = nextCE ? {
              tradingSymbol: nextCE.tradingSymbol || null,
              exchange: nextCE.exchange || null,
              instrumentId: nextCE.token || null,
              tbq: typeof ceTBQ === 'number' ? ceTBQ : 0,
              tsq: typeof ceTSQ === 'number' ? ceTSQ : 0
            } : null;
          } else if (nextCE) {
            // Create new entry with CE data
            exportData.push({
              symbolName: selectedChainInfo.symbolName,
              expiryDate: nextExpiryDate,
              strikePrice: parseInt(ceStrike),
              ce: {
                tradingSymbol: nextCE.tradingSymbol || null,
                exchange: nextCE.exchange || null,
                instrumentId: nextCE.token || null,
                tbq: typeof ceTBQ === 'number' ? ceTBQ : 0,
                tsq: typeof ceTSQ === 'number' ? ceTSQ : 0
              },
              pe: null
            });
          }
        }
      });
      
      if (exportData.length === 0) {
        setExportMessage('❌ No valid data to export');
        setTimeout(() => setExportMessage(''), 3000);
        setIsExporting(false);
        return;
      }
      
      // Send to backend
      const response = await api.post('/bull-bear/export', {
        symbolName: selectedChainInfo.symbolName,
        expiryDate: formattedExpiryDate,
        bullBearData: exportData
      });
      
      if (response.data.success) {
        const result = response.data.data;
        setLastExportSessionId(result.exportSessionId);
        
        // Show success popup
        setPopupMessage(`✅ Success`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        
        // Clear exported data and fetch fresh, then flip to difference view
        setExportedData([]);
        await handleFetchExportedData();
        setIsFlipped(true);
      } else {
        // Show error popup
        setPopupMessage('❌ Export failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (error) {
      console.error('Export error:', error);
      // Show error popup
      setPopupMessage('❌ Export failed: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle flip table toggle
  const handleFlipTable = async () => {
    if (isFlipped) {
      // Switching to live view - just toggle the state
      setIsFlipped(false);
    } else {
      // Switching back to difference view - fetch exported data if not already loaded
      if (exportedData.length === 0) {
        await handleFetchExportedData();
      }
      setIsFlipped(true);
    }
  };

  // Handle master symbol selection
  const handleMasterSymbolChange = async (exchange, masterSymbolName) => {
    setSelectedMasterSymbol(prev => ({...prev, [exchange]: masterSymbolName}));
    
    if (!masterSymbolName) {
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      return;
    }

    try {
      // Search for symbols based on master symbol name
      const params = new URLSearchParams({
        q: masterSymbolName,
        exchange: exchange
      });

      const response = await api.get(`/symbols/search?${params}`);
      
      if (response.data.success) {
        setExchangeSearchResults(prev => ({
          ...prev, 
          [exchange]: response.data.data || []
        }));
      } else {
        console.error('Master symbol search failed:', response.data.error);
        setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      }
    } catch (error) {
      console.error('Master symbol search error:', error);
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
    }
  };

  const handleSearch = async (exchange, query) => {
    if (!query.trim()) {
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      return;
    }

    try {
      const params = new URLSearchParams({
        search: query.trim(),
        exchange: exchange
      });

      const response = await api.get(`/symbols/search?${params}`);
      
      if (response.data.success) {
        setExchangeSearchResults(prev => ({
          ...prev,
          [exchange]: response.data.data || []
        }));
      } else {
        console.error('Search failed:', response.data.error);
        setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      }
    } catch (error) {
      console.error('Search error:', error);
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
    }
  };

  const handleAddToWatchlist = async (symbol) => {
    try {
      // Convert API response format to watchlist format
      const watchlistSymbol = {
        exchange: symbol.exchange,
        instrumentId: symbol.instrument_id,
        tradingSymbol: symbol.tradingSymbol || symbol.trading_symbol, // Handle both formats
        instrumentName: symbol.instrument_name || '',
        // Add any other properties that might be needed
        lotSize: symbol.lot_size,
        tickSize: symbol.tick_size,
        parsed_expiry: symbol.parsed_expiry,
        parsed_strike: symbol.parsed_strike,
        parsed_option_type: symbol.parsed_option_type
      };
      
      // Check if symbol already exists in watchlist using fresh Redux state
      const currentWatchlist = store.getState().watchlist.items;
      const exists = currentWatchlist.some(item => 
        item.exchange === watchlistSymbol.exchange && 
        item.instrumentId === watchlistSymbol.instrumentId
      );
      
      if (!exists) {
        // Dispatch Redux action
        dispatch(addToWatchlist(watchlistSymbol));
        
        // Wait a moment and verify the addition was successful
        await new Promise(resolve => setTimeout(resolve, 20));
        
        // Verify the symbol was actually added to Redux
        const updatedWatchlist = store.getState().watchlist.items;
        const wasAdded = updatedWatchlist.some(item => 
          item.exchange === watchlistSymbol.exchange && 
          item.instrumentId === watchlistSymbol.instrumentId
        );
        
        if (!wasAdded) {
          console.warn(`⚠️ Redux ADD failed for ${watchlistSymbol.tradingSymbol} - retrying...`);
          // Force add again
          dispatch(addToWatchlist(watchlistSymbol));
          await new Promise(resolve => setTimeout(resolve, 20));
        }
        
        // Only subscribe to WebSocket if not building options chain (to prevent interference)
        if (wsConnected && !window.isBuildingOptionsChain) {
          try {
            aliceBlueWS.subscribe([{
              exchange: watchlistSymbol.exchange,
              token: watchlistSymbol.instrumentId
            }]);
          } catch (wsError) {
            console.warn('WebSocket subscription error:', wsError);
            // Don't throw - continue without WS subscription
          }
        }
      }
    } catch (error) {
      console.error('Error in handleAddToWatchlist:', error);
      // Don't rethrow - allow the batch process to continue
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    return new Promise((resolve) => {
      dispatch(removeFromWatchlist(symbol));
      // Unsubscribe from this symbol
      if (wsConnected) {
        aliceBlueWS.unsubscribe([{
          exchange: symbol.exchange,
          token: symbol.instrumentId
        }]);
      }
      // Small delay to ensure Redux action is processed
      setTimeout(resolve, 10);
    });
  };

  const handleBuySell = (symbol, action) => {
    const ltp = getLivePrice(symbol.exchange, symbol.instrumentId);
    setOrderData({
      symbol: symbol,
      action: action,
      quantity: symbol.lotSize || '',
      price: ltp || '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
    setOrderFormVisible(true);
    // Scroll to top of watchlist table
    document.getElementById('order-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Symbol Filter Functions for Options Trading
  const handleSymbolNameChange = async (symbolName, exchange = 'NFO') => {
    setSymbolFilters(prev => ({
      symbolName,
      expiryDate: '',
      optionType: '',
      strikePrice: ''
    }));
    setAvailableExpiries([]);
    setAvailableStrikes([]);
    setFilteredSymbols([]);
    
    // CRITICAL FIX: Update nfoSubTab to match the selected symbolName
    // This ensures the current sub-tab matches the filter selection
    if (exchange === 'NFO' && symbolName) {
      setNfoSubTab(symbolName);
      console.log(`📍 Updated nfoSubTab to match filter selection: ${symbolName}`);
    }

    if (!symbolName) return;

    // Fetch available expiry dates for this symbol
    try {
      const response = await api.get(`/symbol-filters/expiry-dates?symbolName=${symbolName}&exchange=${exchange}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        setAvailableExpiries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching expiry dates:', error);
    }
  };

  const handleExpiryChange = async (expiryDate) => {
    console.log('📅 handleExpiryChange called with:', expiryDate);
    console.log('📅 Current symbolFilters.symbolName:', symbolFilters.symbolName);
    
    setSymbolFilters(prev => ({
      ...prev,
      expiryDate,
      strikePrice: '' // Clear strike price when expiry changes
    }));
    setAvailableStrikes([]);
    setFilteredSymbols([]);

    // Use current symbolName from state, not the updated state (timing issue fix)
    const currentSymbolName = symbolFilters.symbolName;
    
    if (!currentSymbolName || !expiryDate) {
      console.log('⚠️ Missing symbolName or expiryDate:', { currentSymbolName, expiryDate });
      return;
    }

    // Fetch available strike prices for this symbol and expiry
    try {
      console.log('🎯 Fetching strike prices for:', { symbolName: currentSymbolName, expiryDate, exchange: activeTab });
      const response = await api.get(`/symbol-filters/strike-prices?symbolName=${currentSymbolName}&expiryDate=${expiryDate}&exchange=${activeTab}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      console.log('🎯 Strike prices response:', response.data);
      
      if (response.data.success) {
        setAvailableStrikes(response.data.data);
        console.log('✅ Available strikes set:', response.data.data);
      } else {
        console.error('❌ Strike prices fetch failed:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching strike prices:', error);
    }
  };

  const handleStrikePriceChange = (strikePrice) => {
    console.log('🎯 handleStrikePriceChange called with:', strikePrice);
    console.log('🎯 Current symbolFilters before update:', symbolFilters);
    
    setSymbolFilters(prev => {
      const updated = { ...prev, strikePrice };
      console.log('🎯 Updated symbolFilters will be:', updated);
      
      // Validate if all required fields will be present
      const isComplete = updated.symbolName && updated.expiryDate && strikePrice;
      console.log('🎯 Will form be complete?', isComplete);
      
      return updated;
    });
    setFilteredSymbols([]);
  };

  const handleApplyFilters = async (customFilters = null) => {
    // Use custom filters if provided, otherwise use current state
    const filters = customFilters || symbolFilters;
    const { symbolName, expiryDate, strikePrice } = filters;

    console.log('🔍 handleApplyFilters called with:');
    console.log('  - customFilters:', customFilters);
    console.log('  - current symbolFilters state:', symbolFilters);
    console.log('  - final filters being used:', filters);
    console.log('  - individual values:', { symbolName, expiryDate, strikePrice });
    console.log('  - validation check:', {
      hasSymbolName: !!symbolName,
      hasExpiryDate: !!expiryDate,
      hasStrikePrice: !!strikePrice
    });

    if (!symbolName || !expiryDate || !strikePrice) {
      console.error('❌ Validation failed - missing required fields');
      alert('Please select symbol name, expiry date, and strike price');
      return;
    }

    try {
      // Set flag to prevent interference from other operations
      window.isBuildingOptionsChain = true;
      
      const centerStrike = parseInt(strikePrice);
      
      console.log('🔍 Starting Smart Options Chain Builder - Following 8-Step Process');
      console.log('🔍 Symbol:', symbolName, 'Expiry:', expiryDate, 'Center Strike:', centerStrike);
      
      // STEP 1-4: Fetch ALL NFO symbols for this symbol and expiry (CE and PE)
      const [ceResponse, peResponse] = await Promise.all([
        api.get(
          `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${expiryDate}&optionType=CE&exchange=NFO`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        ).catch(err => {
          console.error('Error fetching CE:', err);
          return { data: { success: false, data: [] } };
        }),
        api.get(
          `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${expiryDate}&optionType=PE&exchange=NFO`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        ).catch(err => {
          console.error('Error fetching PE:', err);
          return { data: { success: false, data: [] } };
        })
      ]);
      
      const allCE = ceResponse.data.success ? ceResponse.data.data : [];
      const allPE = peResponse.data.success ? peResponse.data.data : [];
      
      console.log('📦 Fetched CE options:', allCE.length, 'PE options:', allPE.length);
      
      if (allCE.length === 0 || allPE.length === 0) {
        alert('No CE or PE options found for this expiry');
        return;
      }
      
      // STEP 5-6: Extract all available strikes and find tick size
      const extractStrike = (tradingSymbol) => {
        const match = tradingSymbol?.match(/[CP](\d+)$/);
        return match ? parseInt(match[1]) : null;
      };
      
      const ceStrikes = allCE.map(opt => extractStrike(opt.trading_symbol)).filter(Boolean).sort((a, b) => a - b);
      const peStrikes = allPE.map(opt => extractStrike(opt.trading_symbol)).filter(Boolean).sort((a, b) => a - b);
      
      // Get strikes available in BOTH CE and PE
      const commonStrikes = ceStrikes.filter(s => peStrikes.includes(s));
      const uniqueStrikes = [...new Set(commonStrikes)].sort((a, b) => a - b);
      
      console.log('🎯 Available strikes:', uniqueStrikes.length, 'Range:', uniqueStrikes[0], 'to', uniqueStrikes[uniqueStrikes.length - 1]);
      
      if (uniqueStrikes.length < 2) {
        alert('Not enough strikes available');
        return;
      }
      
      // Calculate tick size (difference between consecutive strikes)
      const tickSize = uniqueStrikes[1] - uniqueStrikes[0];
      console.log('📏 Tick size detected:', tickSize);
      
      // STEP 7: Generate exactly 19 strikes (9 below + center + 9 above)
      const targetStrikes = [];
      
      // Always generate exactly 19 strikes, regardless of availability
      for (let i = -9; i <= 9; i++) {
        const strike = centerStrike + (i * tickSize);
        targetStrikes.push(strike);
      }
      
      // Filter to only include strikes that exist in our data
      const availableTargetStrikes = targetStrikes.filter(strike => uniqueStrikes.includes(strike));
      
      console.log('📊 Calculated 19 strikes:', targetStrikes);
      console.log('📊 Available in data:', availableTargetStrikes.length, '/', targetStrikes.length);
      console.log('📊 Available strikes:', availableTargetStrikes);
      
      // STEP 8: Build final symbol list with proper mapping
      const allSymbols = [];
      
      availableTargetStrikes.forEach(strike => {
        // Find CE option
        const ceOption = allCE.find(opt => extractStrike(opt.trading_symbol) === strike);
        // Find PE option  
        const peOption = allPE.find(opt => extractStrike(opt.trading_symbol) === strike);
        
        if (ceOption) {
          // Fix property mapping: backend returns trading_symbol, frontend expects tradingSymbol
          const normalizedCE = {
            ...ceOption,
            tradingSymbol: ceOption.trading_symbol, // Map backend property to frontend expectation
            exchange: 'NFO'
          };
          allSymbols.push(normalizedCE);
          console.log(`✅ Added CE ${strike}:`, normalizedCE.tradingSymbol);
        } else {
          console.warn(`❌ CE not found for strike ${strike}`);
        }
        
        if (peOption) {
          // Fix property mapping: backend returns trading_symbol, frontend expects tradingSymbol
          const normalizedPE = {
            ...peOption,
            tradingSymbol: peOption.trading_symbol, // Map backend property to frontend expectation
            exchange: 'NFO'
          };
          allSymbols.push(normalizedPE);
          console.log(`✅ Added PE ${strike}:`, normalizedPE.tradingSymbol);
        } else {
          console.warn(`❌ PE not found for strike ${strike}`);
        }
      });      console.log(`✅ Total symbols to add: ${allSymbols.length} (${targetStrikes.length} strikes × 2 types)`);

      if (allSymbols.length > 0) {
        // CRITICAL FIX: Only clear options for the CURRENT sub-tab (symbolName)
        // This allows multiple sub-tabs to maintain their own independent CE/PE data
        console.log(`🧹 Clearing existing NFO options for sub-tab: ${symbolName}`);
        
        // Remove ONLY options for the current symbolName (current sub-tab)
        const currentSubTabOptions = watchlist.filter(item => 
          item.exchange === 'NFO' && 
          item.tradingSymbol && 
          item.tradingSymbol.startsWith(symbolName)
        );
        
        console.log(`Found ${currentSubTabOptions.length} existing NFO options for ${symbolName} to remove`);
        
        // Remove them in smaller batches to prevent Redux overwhelm
        const removalBatchSize = 10;
        for (let i = 0; i < currentSubTabOptions.length; i += removalBatchSize) {
          const batch = currentSubTabOptions.slice(i, i + removalBatchSize);
          console.log(`🗑️ Removing batch ${Math.floor(i/removalBatchSize) + 1}/${Math.ceil(currentSubTabOptions.length/removalBatchSize)} (${batch.length} options from ${symbolName})`);
          
          for (const option of batch) {
            try {
              await handleRemoveFromWatchlist(option);
            } catch (removeError) {
              console.error(`Error removing ${option.tradingSymbol}:`, removeError);
              // Continue with next item
            }
          }
          
          // Wait between removal batches
          if (i + removalBatchSize < currentSubTabOptions.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        // Ensure all removals are processed and verify state
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Verify options for this sub-tab are removed
        const freshWatchlistAfterRemoval = store.getState().watchlist.items;
        const remainingForSubTab = freshWatchlistAfterRemoval.filter(item => 
          item.exchange === 'NFO' && 
          item.tradingSymbol &&
          item.tradingSymbol.startsWith(symbolName)
        );
        console.log(`✅ Removal verification: ${remainingForSubTab.length} NFO options for ${symbolName} remain (should be 0)`);
        
        if (remainingForSubTab.length > 0) {
          console.warn(`⚠️ ${remainingForSubTab.length} NFO options for ${symbolName} still in Redux state. Force clearing...`);
          // Force clear any remaining options for this sub-tab
          remainingForSubTab.forEach(option => {
            try {
              dispatch(removeFromWatchlist(option));
            } catch (dispatchError) {
              console.error('Error dispatching remove:', dispatchError);
            }
          });
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Final verification
          const finalCheck = store.getState().watchlist.items.filter(item => 
            item.exchange === 'NFO' && 
            item.tradingSymbol &&
            item.tradingSymbol.startsWith(symbolName)
          );
          console.log(`🔄 Final removal check: ${finalCheck.length} NFO options for ${symbolName} remain`);
        }
        
        console.log(`➕ Adding ${allSymbols.length} new options to watchlist`);
        
        // Add symbols in smaller batches to prevent Redux issues
        let addedCount = 0;
        const batchSize = 3;
        
        for (let i = 0; i < allSymbols.length; i += batchSize) {
          const batch = allSymbols.slice(i, i + batchSize);
          console.log(`📦 Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(allSymbols.length/batchSize)} (${batch.length} symbols)`);
          
          // Process batch
          for (const symbol of batch) {
            try {
              console.log(`⬆️ Adding ${symbol.tradingSymbol} to watchlist...`);
              await handleAddToWatchlist(symbol);
              addedCount++;
              console.log(`✅ Successfully added ${symbol.tradingSymbol} (${addedCount}/${allSymbols.length})`);
            } catch (error) {
              console.error(`❌ Failed to add ${symbol.tradingSymbol}:`, error);
            }
          }
          
          // Verify batch was added to Redux and wait longer between batches
          if (i + batchSize < allSymbols.length) {
            console.log('⏳ Waiting for Redux state to stabilize...');
            
            // Check current Redux state
            const currentCount = store.getState().watchlist.items.filter(item => item.exchange === 'NFO').length;
            console.log(`🔍 Batch verification: Expected ~${addedCount}, Redux has ${currentCount}`);
            
            await new Promise(resolve => setTimeout(resolve, 300)); // Increased delay
          }
        }
        
        console.log(`✅ Successfully added ${addedCount} of ${allSymbols.length} options`);
        
        // Set filtered symbols immediately without waiting for database operations
        setFilteredSymbols([...allSymbols]);
        
        // Save chain info for header display (store both formatted and raw date)
        const expiryLabel = availableExpiries.find(e => e.date === expiryDate)?.formatted || expiryDate;
        setSelectedChainInfo({
          symbolName: symbolName,
          expiryDate: expiryLabel,        // Formatted date for display
          expiryDateRaw: expiryDate,       // Raw date (YYYY-MM-DD) for API comparisons
          centerStrike: centerStrike.toLocaleString()
        });
        
        console.log(`🎉 Smart Options Chain Complete: ${availableTargetStrikes.length} strikes created with ${addedCount} total options for ${symbolName}`);
        
        // Clear the building flag
        window.isBuildingOptionsChain = false;
        
        // Wait longer for all Redux actions to be processed
        const timeoutId = setTimeout(() => {
          try {
            // Force immediate display update with current Redux state
            // Use store.getState() to get fresh state instead of stale closure
            const freshWatchlist = store.getState().watchlist.items;
            // CRITICAL FIX: Only display options for the current symbolName (sub-tab)
            const currentSubTabItems = freshWatchlist.filter(item => 
              item.exchange === 'NFO' && 
              item.tradingSymbol?.startsWith(symbolName)
            );
            setFilteredSymbols([...currentSubTabItems]);
            console.log(`📊 Immediately displaying ${currentSubTabItems.length} options for ${symbolName} from fresh Redux state`);
            console.log(`🔍 Fresh Redux state check: ${symbolName}=${currentSubTabItems.length}`);
            
            if (currentSubTabItems.length === addedCount) {
              console.log(`✅ State synchronization successful for ${symbolName}!`);
            } else {
              console.warn(`⚠️ State sync issue for ${symbolName}: Expected ${addedCount}, got ${currentSubTabItems.length}`);
              console.log('🔄 Attempting final state refresh...');
              // One more attempt with a fresh read
              const finalFreshWatchlist = store.getState().watchlist.items;
              const finalSubTabItems = finalFreshWatchlist.filter(item => 
                item.exchange === 'NFO' && 
                item.tradingSymbol?.startsWith(symbolName)
              );
              setFilteredSymbols([...finalSubTabItems]);
              console.log(`📊 Final display update: ${finalSubTabItems.length} options for ${symbolName} from Redux`);
            }
          } catch (timeoutError) {
            console.error('Error in state sync timeout:', timeoutError);
            // Don't crash - just log the error
          }
        }, 2000); // Increased from 0 to 2000ms
        
        // Cleanup function in case component unmounts
        return () => clearTimeout(timeoutId);
        
        // Do NOT auto-collapse here - user may want to adjust filters
        // setIsFilterCollapsed(true);
      } else {
        alert('No symbols found with the selected criteria.');
        setFilteredSymbols([]);
        window.isBuildingOptionsChain = false;
      }
    } catch (error) {
      console.error('Error searching filtered symbols:', error);
      window.isBuildingOptionsChain = false; // Clear flag on error
      setFilteredSymbols([]); // Clear symbols on error
      alert('Error searching symbols: ' + error.message);
    }
  };
  
  const clearFilters = () => {
    setSymbolFilters({
      symbolName: '',
      expiryDate: '',
      optionType: '',
      strikePrice: ''
    });
    setAvailableExpiries([]);
    setAvailableStrikes([]);
    setFilteredSymbols([]);
  };

  const handleBaseStrikeChange = async (newBaseStrike) => {
    const strikeValue = parseInt(newBaseStrike.replace(/,/g, ''));
    
    console.log(`🎯 Header Base Strike Change Triggered: ${newBaseStrike} → ${strikeValue}`);
    console.log('📋 Current chain info:', selectedChainInfo);
    console.log('🔄 Building status:', window.isBuildingOptionsChain);
    
    if (isNaN(strikeValue)) {
      console.log('❌ Invalid strike value (NaN)');
      alert('Please enter a valid numeric strike price');
      return;
    }
    
    if (!selectedChainInfo) {
      console.log('❌ No chain info available');
      alert('Please build an options chain first');
      return;
    }

    // Guard against multiple simultaneous builds
    if (window.isBuildingOptionsChain) {
      console.log('⏸️ Already building options chain, ignoring header change');
      return;
    }

    console.log(`✅ Processing strike change: ${strikeValue}`);

    // Create the filters object to pass directly to handleApplyFilters
    const updatedFilters = {
      symbolName: selectedChainInfo.symbolName,
      expiryDate: selectedChainInfo.expiryDateRaw, // Use raw date for API
      optionType: '', // Keep empty for both CE and PE
      strikePrice: strikeValue.toString(),
      exchange: 'NFO'
    };

    console.log('📝 Passing filters to handleApplyFilters:', updatedFilters);
    
    // Set building flag and add small delay to ensure state is updated
    window.isBuildingOptionsChain = true;
    console.log(`🚀 Rebuilding options chain with center strike: ${strikeValue}`);
    
    setTimeout(async () => {
      try {
        await handleApplyFilters(updatedFilters); // Pass filters directly
        setEditableBaseStrike(''); // Clear only after successful completion
        
        // Update the state to match what was used
        setSymbolFilters(updatedFilters);
      } catch (error) {
        console.error('❌ Error rebuilding options chain:', error);
        window.isBuildingOptionsChain = false;
      }
    }, 100);
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    
    if (!orderData.symbol || !orderData.quantity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const orderPayload = {
        exchange: orderData.symbol.exchange,
        tradingSymbol: orderData.symbol.tradingSymbol,
        instrumentId: orderData.symbol.instrumentId,
        transactionType: orderData.action, // Fix: use transactionType instead of action
        quantity: parseInt(orderData.quantity),
        price: orderData.orderType === 'MARKET' ? 0 : parseFloat(orderData.price),
        orderType: orderData.orderType,
        validity: orderData.validity,
        product: orderData.product,
        orderComplexity: 'REGULAR', // Add missing field
        deviceId: '123', // Add missing field
        userId: userId // Add userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        alert(`Order placed successfully! Order ID: ${response.data.data.brokerOrderId || 'N/A'}`);
        // Reset form
        setOrderFormVisible(false);
        setOrderData({
          symbol: null,
          action: 'BUY',
          quantity: '',
          price: '',
          orderType: 'LIMIT',
          validity: 'DAY',
          product: 'MIS'
        });
      } else {
        alert('Order failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Order placement error:', error);
      alert('Failed to place order: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelOrder = () => {
    setOrderFormVisible(false);
    setOrderData({
      symbol: null,
      action: 'BUY',
      quantity: '',
      price: '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
  };

  const getLivePrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const livePrice = priceData?.ltp || priceData?.lp;
    
    // If live price exists and is greater than 0, return it
    if (livePrice && livePrice > 0) {
      return livePrice;
    }
    
    // If no LTP, check for close price from WebSocket data
    if (priceData?.c && priceData.c > 0) {
      console.log(`🔍 Using close price for ${key}:`, priceData.c);
      return priceData.c;
    }
    
    // Otherwise, try to get last traded price from watchlist item
    const watchlistItem = watchlist.find(item => 
      item.exchange === exchange && item.instrumentId === token
    );
    
    // Return close price or last price from watchlist data
    const fallbackPrice = watchlistItem?.closePrice || watchlistItem?.lastPrice || null;
    if (fallbackPrice) {
      console.log(`🔍 Using watchlist fallback for ${key}:`, fallbackPrice);
    } else {
      console.log(`⚠️ No price data available for ${key}`, { priceData, watchlistItem });
    }
    
    return fallbackPrice;
  };

  // Helper function to get high price from WebSocket data
  const getHighPrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const highValue = priceData?.h || null;
    
    // Simplified logging - only log once if missing
    if (highValue === null && priceData && !priceData._highLogged) {
      console.log(`⚠️ No high price for ${key}. Available fields:`, Object.keys(priceData));
      priceData._highLogged = true;
    }
    
    return highValue;
  };

  // Helper function to get low price from WebSocket data
  const getLowPrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const lowValue = priceData?.l || null;
    
    // Simplified logging - only log once if missing
    if (lowValue === null && priceData && !priceData._lowLogged) {
      console.log(`⚠️ No low price for ${key}. Available fields:`, Object.keys(priceData));
      priceData._lowLogged = true;
    }
    
    return lowValue;
  };

  const getPriceChange = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const change = livePrices[key]?.pc;
    // Return the change if it exists, otherwise return null
    return (change !== undefined && change !== null) ? change : null;
  };

  // Handle quick order placement
  const handleQuickOrder = async (strikeData, action, optionType) => {
    if (!strikeData) return;

    try {
      const qty = optionType === 'CE' ? parseInt(ceQty) : parseInt(peQty);
      const livePrice = getLivePrice(strikeData.exchange, strikeData.instrumentId);
      const price = livePrice || 0;

      if (!qty || isNaN(qty)) {
        alert('Please enter valid quantity');
        return;
      }

      if (orderType === 'LIMIT' && (!price || price <= 0)) {
        alert('Price not available for LIMIT order');
        return;
      }

      const orderPayload = {
        exchange: strikeData.exchange,
        tradingSymbol: strikeData.tradingSymbol,
        instrumentId: strikeData.instrumentId,
        transactionType: action,
        quantity: qty,
        price: orderType === 'MARKET' ? 0 : price,
        orderType: orderType,
        validity: 'DAY',
        product: productType,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        alert(`${action} order placed successfully!`);
        // Store last direction for Re Enter functionality
        localStorage.setItem(`lastDirection_${optionType}`, action);
        // Refresh positions
        fetchPositions();
      } else {
        alert('Order failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Order placement error:', error);
      alert('Failed to place order: ' + (error.response?.data?.error || error.message));
    }
  };

  // Fetch positions from API (called after order placement)
  const fetchPositions = async () => {
    try {
      const response = await api.get('/portfolio/positions');
      if (response.data.success && response.data.data) {
        // Filter and update raw positions
        const filtered = response.data.data.filter(pos => {
          if (activeTab === 'NFO' && nfoSubTab) {
            return pos.exchange === 'NFO' && pos.tradingSymbol?.startsWith(nfoSubTab);
          } else if (activeTab === 'BFO' && bfoSubTab) {
            return pos.exchange === 'BFO' && pos.tradingSymbol?.startsWith(bfoSubTab);
          }
          return pos.exchange === activeTab;
        });
        setRawPositions(filtered);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  // Calculate total MTM
  const calculateTotalMTM = () => {
    if (positions.length === 0) return '0';
    const total = positions.reduce((sum, pos) => sum + parseFloat(pos.netPnl || 0), 0);
    return total.toFixed(2);
  };

  // Handle exit position
  const handleExitPosition = async (position) => {
    if (!window.confirm(`Exit position for ${position.symbol}?`)) return;

    try {
      const exitAction = position.transaction === 'Buy' ? 'SELL' : 'BUY';
      const orderPayload = {
        exchange: position.rawData.exchange,
        tradingSymbol: position.rawData.tradingSymbol,
        instrumentId: position.rawData.token,
        transactionType: exitAction,
        quantity: position.quantity,
        price: 0,
        orderType: 'MARKET',
        validity: 'DAY',
        product: position.rawData.product || productType,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        alert('Exit order placed successfully!');
        fetchPositions();
      } else {
        alert('Exit order failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Exit order error:', error);
      alert('Failed to place exit order: ' + (error.response?.data?.error || error.message));
    }
  };

  // Store raw positions data separately
  const [rawPositions, setRawPositions] = useState([]);

  // Fetch positions from API (infrequent - only when needed)
  useEffect(() => {
    const fetchPositionsData = async () => {
      try {
        const response = await api.get('/portfolio/positions');
        if (response.data.success && response.data.data) {
          // Filter by exchange/sub-tab
          const filtered = response.data.data.filter(pos => {
            if (activeTab === 'NFO' && nfoSubTab) {
              return pos.exchange === 'NFO' && pos.tradingSymbol?.startsWith(nfoSubTab);
            } else if (activeTab === 'BFO' && bfoSubTab) {
              return pos.exchange === 'BFO' && pos.tradingSymbol?.startsWith(bfoSubTab);
            }
            return pos.exchange === activeTab;
          });
          setRawPositions(filtered);
        } else if (response.data.sessionExpired) {
          // Session expired - show alert and redirect to login
          alert('Your session has expired. Please login again.');
          window.location.href = '/login';
        } else if (response.data.warning) {
          console.warn('Positions API warning:', response.data.warning);
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
        // Check if it's a 401 error (session expired)
        if (error.response?.status === 401) {
          alert('Your session has expired. Please login again.');
          window.location.href = '/login';
        }
      }
    };

    if (activeTab === 'NFO' || activeTab === 'BFO') {
      fetchPositionsData();
      const interval = setInterval(fetchPositionsData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, nfoSubTab, bfoSubTab]);

  // Update positions display with live prices (frequent - whenever prices change)
  useEffect(() => {
    if (rawPositions.length > 0) {
      // Debug: Log first position to see field names
      console.log('Raw Position Data Sample:', rawPositions[0]);
      
      const transformedPositions = rawPositions.map(pos => {
        const key = `${pos.exchange}|${pos.token}`;
        const priceData = livePrices[key];
        const ltp = priceData?.ltp || priceData?.lp || parseFloat(pos.ltp || 0);
        
        // Get buy and sell quantities - AliceBlue uses buyQty/sellQty
        const buyQty = parseFloat(pos.buyQty || pos.buyQuantity || pos.buyqty || pos.daybuyqty || 0);
        const sellQty = parseFloat(pos.sellQty || pos.sellQuantity || pos.sellqty || pos.daysellqty || 0);
        const netQty = parseInt(pos.netQty || pos.netQuantity || pos.netqty || (buyQty - sellQty));
        
        // Get buy and sell prices
        // Your backend API returns: dayBuyPrice, daySellPrice (total price, not avg)
        // AliceBlue API returns: buyPrice, sellPrice, mtmBuyPrice, mtmSellprice (average price per unit)
        // We need to calculate average if we have dayBuyPrice and quantities
        
        let buyAvg = 0;
        let sellAvg = 0;
        
        // Check for AliceBlue fields first (these are average prices)
        if (pos.buyPrice || pos.mtmBuyPrice) {
          buyAvg = parseFloat(pos.buyPrice || pos.mtmBuyPrice || 0);
        } else if (pos.dayBuyPrice && buyQty > 0) {
          // If we have total price and quantity, calculate average
          buyAvg = parseFloat(pos.dayBuyPrice) / buyQty;
        }
        
        if (pos.sellPrice || pos.mtmSellprice) {
          sellAvg = parseFloat(pos.sellPrice || pos.mtmSellprice || 0);
        } else if (pos.daySellPrice && sellQty > 0) {
          // If we have total price and quantity, calculate average
          sellAvg = parseFloat(pos.daySellPrice) / sellQty;
        }
        
        // Calculate Day's P&L (Realized) and Net P&L (Unrealized)
        const dayPnl = parseFloat(pos.realizedPnl || pos.dayPnL || pos.rpnl || pos.realizedpnl || 0);
        const netPnl = parseFloat(pos.unrealizedPnl || pos.netPnL || pos.pnl || pos.unrealizedpnl || 0);
        
        // Calculate MTM based on net quantity and LTP
        let mtm = 0;
        if (netQty !== 0) {
          const avgPrice = netQty > 0 ? buyAvg : sellAvg;
          mtm = (ltp - avgPrice) * Math.abs(netQty);
        }
        
        // Product type (NRML, MIS, CNC)
        const productType = pos.product || pos.prd || 'NRML';
        
        // Extract option type from trading symbol (CE/PE)
        let optionType = '';
        if (pos.tradingSymbol || pos.tradingsymbol) {
          const symbol = pos.tradingSymbol || pos.tradingsymbol;
          if (symbol.includes('CE')) optionType = 'CE';
          else if (symbol.includes('PE')) optionType = 'PE';
        }
        
        return {
          type: productType,
          instrument: pos.tradingSymbol || pos.tradingsymbol || pos.displayName,
          optionType: optionType,
          qty: Math.abs(netQty),
          buyQty: buyQty,
          sellQty: sellQty,
          buyAvg: buyAvg ? buyAvg.toFixed(2) : '0.00',
          sellAvg: sellAvg ? sellAvg.toFixed(2) : '0.00',
          ltp: ltp ? ltp.toFixed(2) : '-',
          dayPnl: dayPnl.toFixed(2),
          netPnl: netPnl.toFixed(2),
          mtm: mtm.toFixed(0),
          transaction: netQty > 0 ? 'Buy' : 'Sell',
          rawData: pos
        };
      });
      setPositions(transformedPositions);
    }
  }, [rawPositions, livePrices]);

  // Filter watchlist by active tab
  const getFilteredWatchlist = () => {
    if (activeTab === 'ALL') {
      return watchlist;
    }
    return watchlist.filter(item => item.exchange === activeTab);
  };

  // Split watchlist into CE and PE for NFO
  const getOptionsWatchlist = () => {
    let nfoWatchlist = watchlist.filter(item => item.exchange === 'NFO');
    
    // Filter by NFO sub-tab if selected
    if (nfoSubTab) {
      nfoWatchlist = nfoWatchlist.filter(item => 
        item.tradingSymbol.startsWith(nfoSubTab)
      );
    }
    
    const ceOptions = nfoWatchlist.filter(item => {
      // Look for 'C' followed by digits (CE options)
      return /C\d+$/.test(item.tradingSymbol);
    }).sort((a, b) => {
      // Sort CE options in ascending order by strike price
      const strikeA = parseInt(a.tradingSymbol.match(/C(\d+)$/)?.[1] || 0);
      const strikeB = parseInt(b.tradingSymbol.match(/C(\d+)$/)?.[1] || 0);
      return strikeA - strikeB;
    });
    
    const peOptions = nfoWatchlist.filter(item => {
      // Look for 'P' followed by digits (PE options)  
      return /P\d+$/.test(item.tradingSymbol);
    }).sort((a, b) => {
      // Sort PE options in descending order by strike price
      const strikeA = parseInt(a.tradingSymbol.match(/P(\d+)$/)?.[1] || 0);
      const strikeB = parseInt(b.tradingSymbol.match(/P(\d+)$/)?.[1] || 0);
      return strikeB - strikeA;
    });
    
    // Only log when building flag is not set to reduce console spam
    if (!window.isBuildingOptionsChain) {
      console.log('📊 Options watchlist:', {
        totalNFO: nfoWatchlist.length,
        ceOptions: ceOptions.length,
        peOptions: peOptions.length
      });
    }
    
    return { ceOptions, peOptions };
  };

  const filteredWatchlist = getFilteredWatchlist();
  const { ceOptions, peOptions } = getOptionsWatchlist();

  // Track MTM direction changes and show notifications
  useEffect(() => {
    // Calculate current MTM
    const currentCESum = ceOptions.reduce((sum, option) => {
      const ltp = getLivePrice(option.exchange, option.instrumentId);
      return sum + (ltp || 0);
    }, 0);
    
    const currentPESum = peOptions.reduce((sum, option) => {
      const ltp = getLivePrice(option.exchange, option.instrumentId);
      return sum + (ltp || 0);
    }, 0);
    
    const ceMTM = currentCESum - baselineCESum;
    const peMTM = currentPESum - baselinePESum;
    
    // Track CE direction
    if (baselineCESum > 0) {
      const newCEDirection = ceMTM > 5 ? 'up' : ceMTM < -5 ? 'down' : ceDirection;
      
      // Check if direction changed
      if (ceDirection && newCEDirection !== ceDirection && newCEDirection) {
        const now = Date.now();
        const notificationsDisabled = disableNotificationsUntil && now < disableNotificationsUntil;
        const key = `CE_${ceDirection}_to_${newCEDirection}_${Math.floor(now / 60000)}`; // Unique per minute
        
        // DISABLED: Direction notification for future use
        // if (!notificationsDisabled && !notificationsDismissed[key]) {
        //   const ceLTP = selectedCEStrike ? getLivePrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId) : null;
        //   const ceStrike = selectedCEStrike?.strike || 'N/A';
        //   
        //   setDirectionNotificationData({
        //     type: newCEDirection === 'up' ? 'BUY' : 'SELL',
        //     message: newCEDirection === 'up' 
        //       ? `Market is Up trend - Please BUY`
        //       : `Market is Down trend - Please SELL`,
        //     symbol: `${ceStrike}CE`,
        //     ltp: ceLTP?.toFixed(2) || 'N/A',
        //     optionType: 'CE',
        //     strike: ceStrike,
        //     dismissKey: key
        //   });
        //   setShowDirectionNotification(true);
        // }
      }
      
      if (newCEDirection !== ceDirection) {
        setCEDirection(newCEDirection);
      }
    }
    
    // Track PE direction
    if (baselinePESum > 0) {
      const newPEDirection = peMTM > 5 ? 'up' : peMTM < -5 ? 'down' : peDirection;
      
      // Check if direction changed
      if (peDirection && newPEDirection !== peDirection && newPEDirection) {
        const now = Date.now();
        const notificationsDisabled = disableNotificationsUntil && now < disableNotificationsUntil;
        const key = `PE_${peDirection}_to_${newPEDirection}_${Math.floor(now / 60000)}`;
        
        // DISABLED: Direction notification for future use
        // if (!notificationsDisabled && !notificationsDismissed[key]) {
        //   const peLTP = selectedPEStrike ? getLivePrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId) : null;
        //   const peStrike = selectedPEStrike?.strike || 'N/A';
        //   
        //   setDirectionNotificationData({
        //     type: newPEDirection === 'up' ? 'BUY' : 'SELL',
        //     message: newPEDirection === 'up' 
        //       ? `Market is Up trend - Please BUY`
        //       : `Market is Down trend - Please SELL`,
        //     symbol: `${peStrike}PE`,
        //     ltp: peLTP?.toFixed(2) || 'N/A',
        //     optionType: 'PE',
        //     strike: peStrike,
        //     dismissKey: key
        //   });
        //   setShowDirectionNotification(true);
        // }
      }
      
      if (newPEDirection !== peDirection) {
        setPEDirection(newPEDirection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePrices, baselineCESum, baselinePESum, selectedCEStrike, selectedPEStrike, disableNotificationsUntil]);

  // Helper function to render watchlist table
  const renderWatchlistTable = (symbols, title, optionType = null) => {
    // Calculate total LTP sum
    const totalLTP = symbols.reduce((sum, item) => {
      const ltp = getLivePrice(item.exchange, item.instrumentId);
      return sum + (ltp || 0);
    }, 0);

    return (
      <div>
        <div className="bg-white rounded-lg shadow-sm border">
          <div className={`px-2 font-semibold text-[10px] rounded-t-lg h-[32px] flex items-center ${optionType === 'CE' ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' : optionType === 'PE' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white'}`}>
            {title} ({symbols.length})
          </div>
          {symbols.length > 0 ? (
            <div>
              <table className="w-full table-fixed">
                <thead className={`${optionType === 'CE' ? 'bg-green-50' : optionType === 'PE' ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <tr className="text-[10px] font-semibold ${optionType === 'CE' ? 'text-green-700' : optionType === 'PE' ? 'text-red-700' : 'text-gray-700'} h-[28px]">
                    <th className="text-center py-0.5 px-0.5 w-1/2">Strike</th>
                    <th className="text-center py-0.5 px-0.5 w-1/2">LTP</th>
                  </tr>
                </thead>
                <tbody>
                  {symbols.map((item, idx) => {
                    const ltp = getLivePrice(item.exchange, item.instrumentId);
                    
                    // Extract strike price from trading symbol
                    const extractStrikePrice = (symbol) => {
                      // Match pattern like NIFTY25NOV25C23450 -> 23450
                      const match = symbol.match(/[CP](\d+)$/);
                      return match ? parseInt(match[1]) : '—';
                    };
                    
                    const strikePrice = extractStrikePrice(item.tradingSymbol);
                    
                    return (
                      <tr 
                        key={`${item.exchange}-${item.instrumentId}`} 
                        className="border-b border-gray-100 hover:bg-blue-50 hover:shadow-sm cursor-pointer transition-all duration-150 h-[28px]"
                        onDoubleClick={() => {
                          if (optionType === 'CE') {
                            setSelectedCEStrike({ ...item, strike: strikePrice, ltp });
                            if (!ceQty) setCEQty(item.lotSize || '25');
                          } else if (optionType === 'PE') {
                            setSelectedPEStrike({ ...item, strike: strikePrice, ltp });
                            if (!peQty) setPEQty(item.lotSize || '25');
                          }
                        }}
                      >
                        <td className="text-center py-0.5 px-0.5 font-semibold text-xs">{strikePrice}</td>
                        <td className="text-center py-0.5 px-0.5">
                          {ltp ? (
                            <div className="font-semibold text-xs">{ltp.toFixed(2)}</div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total Sum Row - Hidden but calculation still runs for Quick Order Entry */}
                  <tr className={`hidden border-t-2 ${optionType === 'CE' ? 'bg-green-50' : optionType === 'PE' ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <td className="text-center py-0.5 px-0.5 font-bold text-xs">TOTAL</td>
                    <td className="text-center py-0.5 px-0.5">
                      <div className="font-bold text-sm text-blue-600">
                        {totalLTP.toFixed(2)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No {optionType || 'symbols'} in watchlist</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main component return with modern UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      {/* Modern Header */}
      

      {/* Popup Notification */}
      {showPopup && (
        <div className="fixed top-4 right-4 z-50 animate-fadeIn">
          <div className={`px-6 py-4 rounded-lg shadow-2xl border-2 ${
            popupType === 'success' 
              ? 'bg-green-50 border-green-500 text-green-800' 
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {popupType === 'success' ? '✅' : '❌'}
              </span>
              <span className="font-semibold text-base">{popupMessage}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Direction Change Notification */}
      {showDirectionNotification && directionNotificationData && (
        <div className="fixed top-20 right-4 z-50 animate-fadeIn">
          <div className={`px-6 py-4 rounded-lg shadow-2xl border-2 ${
            directionNotificationData.type === 'BUY'
              ? 'bg-green-50 border-green-500 text-green-800'
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {directionNotificationData.type === 'BUY' ? '📈' : '📉'}
                </span>
                <div className="flex-1">
                  <div className="font-semibold text-base">{directionNotificationData.message}</div>
                  <div className="text-sm mt-1">
                    <span className="font-bold">{directionNotificationData.symbol}</span>
                    {' - LTP: '}
                    <span className="font-bold">{directionNotificationData.ltp}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-300">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        const twoHoursFromNow = Date.now() + (2 * 60 * 60 * 1000);
                        setDisableNotificationsUntil(twoHoursFromNow);
                        localStorage.setItem('advancedTrading_disableNotificationsUntil', twoHoursFromNow.toString());
                      } else {
                        setDisableNotificationsUntil(null);
                        localStorage.removeItem('advancedTrading_disableNotificationsUntil');
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span>Don't show for 2 hours</span>
                </label>
                
                <button
                  onClick={() => {
                    setShowDirectionNotification(false);
                    if (directionNotificationData.dismissKey) {
                      setNotificationsDismissed(prev => ({
                        ...prev,
                        [directionNotificationData.dismissKey]: true
                      }));
                    }
                  }}
                  className="px-4 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm font-semibold"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Order Panel */}
      <OrderPanel
        symbol={selectedSymbol}
        isOpen={isOrderPanelOpen}
        onClose={() => {
          setIsOrderPanelOpen(false);
          setSelectedSymbol(null);
        }}
      />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-lg font-bold text-gray-800">Trading View</h1>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded text-xs ${wsConnected ? 'bg-green-100 text-green-800' : connectionError ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {wsConnected ? '🟢 Live Data Connected' : connectionError ? `❌ ${connectionError}` : `⚠️ ${wsStatus}`}
            </div>
            {!wsConnected && (
              <button
                onClick={async () => {
                  setWsStatus('Reconnecting...');
                  setConnectionError(null);
                  try {
                    await aliceBlueWS.connect();
                    if (aliceBlueWS.isConnected) {
                      setWsConnected(true);
                      setWsStatus('Connected');
                    }
                  } catch (err) {
                    console.error('Reconnect failed:', err);
                    setWsStatus('Connection Failed');
                    setConnectionError('WebSocket connection failed - Working without live data');
                  }
                }}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                title="Reconnect to WebSocket"
              >
                🔄 Reconnect
              </button>
            )}
          </div>
          {connectionError && (
            <div className="text-xs text-red-600 max-w-md">
              Error: {connectionError}
            </div>
          )}
        </div>
      </div>

      {/* Modern Exchange Navigation */}
      <div className={`${uiStyles.modernCard} mb-6 p-6`}>
        {/* Header with Collapse Button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Exchange & Symbols</h3>
          </div>
          <button
            onClick={() => {
              setIsExchangeTabsCollapsed(!isExchangeTabsCollapsed);
              localStorage.setItem('advancedTrading_exchangeTabsCollapsed', (!isExchangeTabsCollapsed).toString());
            }}
            className={`${uiStyles.modernButton} px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
              isExchangeTabsCollapsed 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="flex items-center space-x-2">
              <span>{isExchangeTabsCollapsed ? '▼ Expand' : '▲ Collapse'}</span>
            </span>
          </button>
        </div>

        {!isExchangeTabsCollapsed && (
          <>
            <div className="grid grid-cols-6 gap-3 mb-4">
          {['NSE', 'NFO', 'BSE', 'BFO', 'MCX', 'CDS'].map(exchange => {
            const count = watchlist.filter(item => item.exchange === exchange).length;
            
            return (
              <button
                key={exchange}
                onClick={() => {
                  setActiveTab(exchange);
                  // Auto-select first NFO symbol as sub-tab
                  if (exchange === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0) {
                    const firstSymbol = masterSymbolsByExchange['NFO'][0].symbol_name;
                    setNfoSubTab(firstSymbol);
                    // Auto-set symbol filter
                    handleSymbolNameChange(firstSymbol, 'NFO');
                  }
                }}
                className={`${uiStyles.modernButton} relative p-4 rounded-2xl text-sm font-bold transition-all duration-300 ${
                  activeTab === exchange
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-2xl scale-105'
                    : 'bg-white/80 text-gray-700 border border-gray-200 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-600 hover:text-white hover:shadow-xl hover:scale-102'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <span className="text-lg">{exchange}</span>
                  {count > 0 && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                      {count}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* NFO Symbol Sub-tabs - Modern Grid Layout */}
        {activeTab === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-4 border border-purple-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center space-x-2">
              <span>📊</span>
              <span>NFO Instruments</span>
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {(() => {
              // Sort symbols based on custom order
              const sortedSymbols = [...masterSymbolsByExchange['NFO']].sort((a, b) => {
                const indexA = nfoSymbolsOrder.indexOf(a.id);
                const indexB = nfoSymbolsOrder.indexOf(b.id);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              });

              return sortedSymbols.map((symbol) => {
                const nfoCount = watchlist.filter(item => 
                  item.exchange === 'NFO' && 
                  item.tradingSymbol.startsWith(symbol.symbol_name)
                ).length;
                
                return (
                  <button
                    key={symbol.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, symbol.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, symbol.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => {
                      setNfoSubTab(symbol.symbol_name);
                      handleSymbolNameChange(symbol.symbol_name, 'NFO');
                    }}
                    className={`px-2 py-1 rounded font-semibold text-xs transition-colors cursor-move relative ${
                      nfoSubTab === symbol.symbol_name
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${draggedSymbolId === symbol.id ? 'opacity-50' : ''}`}
                  >
                    {symbol.symbol_name}
                    {nfoCount > 0 && (
                      <span className={`absolute -top-1 -right-1 text-[10px] px-1 py-0.5 rounded-full ${
                        nfoSubTab === symbol.symbol_name
                          ? 'bg-green-400 text-white'
                          : 'bg-red-500 text-white'
                      }`}>
                        {nfoCount}
                      </span>
                    )}
                  </button>
                );
              });
            })()}
            </div>
          </div>
        )}

        {/* BFO Symbol Sub-tabs - Modern Grid Layout */}
        {activeTab === 'BFO' && masterSymbolsByExchange['BFO']?.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-4 border border-orange-100">
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center space-x-2">
              <span>📊</span>
              <span>BFO Instruments</span>
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {(() => {
              // Sort symbols based on custom order
              const sortedSymbols = [...masterSymbolsByExchange['BFO']].sort((a, b) => {
                const indexA = bfoSymbolsOrder.indexOf(a.id);
                const indexB = bfoSymbolsOrder.indexOf(b.id);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              });

              return sortedSymbols.map((symbol) => {
                const bfoCount = watchlist.filter(item => 
                  item.exchange === 'BFO' && 
                  item.tradingSymbol.startsWith(symbol.symbol_name)
                ).length;
                
                return (
                  <button
                    key={symbol.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, symbol.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, symbol.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => {
                      setBfoSubTab(symbol.symbol_name);
                      handleSymbolNameChange(symbol.symbol_name, 'BFO');
                    }}
                    className={`px-2 py-1 rounded font-semibold text-xs transition-colors cursor-move relative ${
                      bfoSubTab === symbol.symbol_name
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${draggedSymbolId === symbol.id ? 'opacity-50' : ''}`}
                  >
                    {symbol.symbol_name}
                    {bfoCount > 0 && (
                      <span className={`absolute -top-1 -right-1 text-[10px] px-1 py-0.5 rounded-full ${
                        bfoSubTab === symbol.symbol_name
                          ? 'bg-orange-400 text-white'
                          : 'bg-red-500 text-white'
                      }`}>
                        {bfoCount}
                      </span>
                    )}
                  </button>
                );
              });
            })()}
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Trading View Watchlist */}
      <div>
        {orderFormVisible && (
          <div id="order-form-section" className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Place Order - {orderData.symbol?.tradingSymbol}
            </h3>
            
            <form onSubmit={handleOrderSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Transaction Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
                <select
                  value={orderData.action}
                  onChange={(e) => setOrderData({...orderData, action: e.target.value})}
                  className="input w-full"
                >
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <input
                  type="number"
                  value={orderData.quantity}
                  onChange={(e) => setOrderData({...orderData, quantity: e.target.value})}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>

              {/* Order Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                <select
                  value={orderData.orderType}
                  onChange={(e) => setOrderData({...orderData, orderType: e.target.value})}
                  className="input w-full"
                >
                  <option value="MARKET">Market</option>
                  <option value="LIMIT">Limit</option>
                  <option value="SL">Stop Loss</option>
                  <option value="SLM">SL Market</option>
                </select>
              </div>

              {/* Price */}
              {orderData.orderType !== 'MARKET' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <input
                    type="number"
                    step="0.05"
                    value={orderData.price}
                    onChange={(e) => setOrderData({...orderData, price: e.target.value})}
                    className="input w-full"
                    required={orderData.orderType !== 'MARKET'}
                  />
                </div>
              )}

              {/* Product
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
                <select
                  value={orderData.product}
                  onChange={(e) => setOrderData({...orderData, product: e.target.value})}
                  className="input w-full"
                >
                  <option value="MIS">MIS (Intraday)</option>
                  <option value="NRML">NRML (Normal)</option>
                  <option value="CNC">CNC (Delivery)</option>
                </select>
              </div> */}

              {/* Validity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validity</label>
                <select
                  value={orderData.validity}
                  onChange={(e) => setOrderData({...orderData, validity: e.target.value})}
                  className="input w-full"
                >
                  <option value="DAY">Day</option>
                  <option value="IOC">IOC</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="md:col-span-3 flex space-x-4">
                <button
                  type="submit"
                  className={`btn flex-1 ${
                    orderData.action === 'BUY' ? 'btn-success' : 'btn-danger'
                  }`}
                >
                  {orderData.action === 'BUY' ? 'BUY' : 'SELL'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>

            {/* Symbol Info */}
            <div className="text-sm text-gray-600 bg-white p-2 rounded mt-4">
              <strong>Symbol:</strong> {orderData.symbol?.tradingSymbol} | 
              <strong> Exchange:</strong> {orderData.symbol?.exchange} | 
              <strong> LTP:</strong> {getLivePrice(orderData.symbol?.exchange, orderData.symbol?.instrumentId)?.toFixed(2) || '—'}
            </div>
          </div>
        )}

        {/* Master Symbol Selection for Active Exchange (Hide for NFO) */}
        {activeTab !== 'NFO' && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4 mb-3">
              <h4 className="font-semibold text-gray-700">Select Symbol for {activeTab}</h4>
            </div>
            
            <div className="flex space-x-4">
              {/* Master Symbol Dropdown */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Master Symbol
                </label>
                <select
                  value={selectedMasterSymbol[activeTab] || ''}
                  onChange={(e) => handleMasterSymbolChange(activeTab, e.target.value)}
                  className="input w-full"
                >
                  <option value="">Select a symbol...</option>
                  {(masterSymbolsByExchange[activeTab] || []).map((symbol) => (
                    <option key={symbol.id} value={symbol.symbol_name}>
                      {symbol.display_name} ({symbol.symbol_name})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Manual Search Input */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Search Manually
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={exchangeSearchQueries[activeTab] || ''}
                    onChange={(e) => setExchangeSearchQueries(prev => ({
                      ...prev,
                      [activeTab]: e.target.value
                    }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch(activeTab, exchangeSearchQueries[activeTab] || '');
                      }
                    }}
                    placeholder={`Search ${activeTab} symbols...`}
                    className="input flex-1"
                  />
                  <button
                    onClick={() => handleSearch(activeTab, exchangeSearchQueries[activeTab] || '')}
                    className="btn btn-primary"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Options Filter (for NFO/derivatives) - Merged Compact Version */}
        {(activeTab === 'NFO' || activeTab === 'BFO') && (  
          <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
            {/* Single Header Bar with Collapse Button */}
            <div className="flex items-center justify-between bg-blue-100 p-2 rounded border border-blue-300 mb-2">
              <h4 className="font-semibold text-gray-700 text-sm">🚀 Select Expiry & Strike Price</h4>
              <button
                onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1"
              >
                {isFilterCollapsed ? '▼ Expand' : '▲ Collapse'}
              </button>
            </div>
            
            {!isFilterCollapsed && (
              <>
              {/* 50/50 Layout: Left = Filters, Right = Chain Info */}
              <div className="bg-white p-3 rounded border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  {/* LEFT SIDE 50%: Filter Controls + Add Button */}
                  <div className="bg-gray-50 p-3 rounded border border-gray-300">
                    <div className="flex items-end gap-2 h-full">
                      {/* Symbol Name Selection - Hide when NFO sub-tab is active */}
                      {!nfoSubTab && (
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Symbol
                          </label>
                          <select
                            value={symbolFilters.symbolName}
                            onChange={(e) => handleSymbolNameChange(e.target.value, activeTab)}
                            className="input w-full text-xs py-2 px-2"
                          >
                            <option value="">Select</option>
                            {(masterSymbolsByExchange[activeTab] || []).map((symbol) => (
                              <option key={symbol.id} value={symbol.symbol_name}>
                                {symbol.symbol_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Expiry Date Selection */}
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Expiry
                        </label>
                        <select
                          value={symbolFilters.expiryDate}
                          onChange={(e) => handleExpiryChange(e.target.value)}
                          className="input w-full text-xs py-2 px-2"
                          disabled={!symbolFilters.symbolName && !nfoSubTab}
                        >
                          <option value="">Select</option>
                          {availableExpiries.map((expiry, idx) => (
                            <option key={idx} value={expiry.date}>
                              {expiry.formatted}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Strike Selection */}
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Strike
                        </label>
                        <select
                          value={symbolFilters.strikePrice}
                          onChange={(e) => handleStrikePriceChange(e.target.value)}
                          className="input w-full text-xs py-2 px-2"
                          disabled={!symbolFilters.expiryDate}
                        >
                          <option value="">Select</option>
                          {availableStrikes.map((strike, idx) => (
                            <option key={idx} value={strike}>
                              {strike}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Add Button */}
                      <div className="flex-none" style={{width: '80px'}}>
                        <button
                          onClick={() => {
                            console.log('🚀 Button clicked - current symbolFilters state:', symbolFilters);
                            handleApplyFilters();
                          }}
                          disabled={!symbolFilters.symbolName || !symbolFilters.expiryDate || !symbolFilters.strikePrice}
                          className="btn btn-primary text-xs px-2 py-2 w-full h-[26px]"
                        >
                          🚀 Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE 50%: Chain Info + Analyse Button */}
                  <div className="bg-blue-50 p-3 rounded border border-blue-300">
                    <div className="flex items-end gap-2 h-full">
                      {selectedChainInfo ? (
                        <>
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Symbol</div>
                            <div className="text-sm font-bold text-blue-800 bg-white px-2 py-2 rounded border border-blue-200 h-[26px] flex items-center">
                              {selectedChainInfo.symbolName}
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Expiry Date</div>
                            <div className="text-sm font-semibold text-blue-700 bg-white px-2 py-2 rounded border border-blue-200 h-[26px] flex items-center">
                              {selectedChainInfo.expiryDate}
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Spot Price</div>
                            <input
                              type="text"
                              value={editableBaseStrike || selectedChainInfo.centerStrike}
                              onChange={(e) => {
                                console.log('📝 Base strike input changed:', e.target.value);
                                setEditableBaseStrike(e.target.value);
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  console.log('⌨️ Enter pressed, value:', e.target.value);
                                  e.preventDefault();
                                  handleBaseStrikeChange(e.target.value);
                                }
                              }}
                              onBlur={(e) => {
                                console.log('👁️ Input blurred, value:', e.target.value);
                                if (e.target.value && e.target.value !== selectedChainInfo.centerStrike) {
                                  handleBaseStrikeChange(e.target.value);
                                }
                              }}
                              onFocus={(e) => {
                                console.log('🎯 Input focused');
                                e.target.select();
                              }}
                              placeholder="Strike"
                              className="w-full px-2 py-2 text-sm font-semibold text-blue-700 border border-blue-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 h-[26px]"
                            />
                          </div>

                          {/* Analyse Button */}
                          {(ceOptions.length > 0 || peOptions.length > 0) && (
                            <div className="flex-1">
                              <button 
                              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-2 py-2 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105 text-xs w-full h-[26px]"
                            onClick={async () => {
                              try {
                                console.log('📊 Starting options analysis export...');
                                
                                const extractStrikeFromSymbol = (symbol) => {
                                  if (!symbol) return null;
                                  // NFO: NIFTY25DEC25C26000 -> 26000
                                  let match = symbol.match(/[CP](\d+)$/);
                                  if (match) return parseInt(match[1]);
                                  
                                  // BFO: SENSEX25DEC72800CE -> 72800
                                  match = symbol.match(/(\d{3,5})[CP]E$/);
                                  if (match) return parseInt(match[1]);
                                  
                                  return null;
                                };
                                
                                const extractExpiryFromSymbol = (symbol) => {
                                  if (!symbol) return null;
                                  
                                  // NFO: NIFTY02DEC25C26000 -> 2025-12-02
                                  let match = symbol.match(/(\d{2})([A-Z]{3})(\d{2})[CP]/);
                                  if (match) {
                                    const day = match[1];
                                    const monthStr = match[2];
                                    const year = '20' + match[3];
                                    
                                    const monthMap = {
                                      'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
                                      'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
                                      'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
                                    };
                                    
                                    return `${year}-${monthMap[monthStr]}-${day}`;
                                  }
                                  
                                  // BFO: SENSEX24DEC72800CE -> 2025-12-24
                                  match = symbol.match(/(\d{2})([A-Z]{3})(\d+)[CP]E$/);
                                  if (match) {
                                    const day = match[1];
                                    const monthStr = match[2];
                                    const currentYear = new Date().getFullYear();
                                    
                                    const monthMap = {
                                      'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
                                      'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
                                      'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
                                    };
                                    
                                    const month = monthMap[monthStr];
                                    if (month) {
                                      return `${currentYear}-${month}-${day}`;
                                    }
                                  }
                                  
                                  return null;
                                };
                                
                                const extractedExpiryDate = selectedChainInfo?.expiryDateRaw || (ceOptions.length > 0 ? 
                                  extractExpiryFromSymbol(ceOptions[0].tradingSymbol) : 
                                  (peOptions.length > 0 ? extractExpiryFromSymbol(peOptions[0].tradingSymbol) : ''));
                                
                                console.log('📅 Extracted expiry date:', extractedExpiryDate);
                                
                                const ceOptionsWithLTP = ceOptions.map(option => ({
                                  exchange: option.exchange || 'NFO',
                                  tradingSymbol: option.tradingSymbol,
                                  strikePrice: extractStrikeFromSymbol(option.tradingSymbol),
                                  ltp: getLivePrice(option.exchange, option.instrumentId)
                                }));
                                
                                const peOptionsWithLTP = peOptions.map(option => ({
                                  exchange: option.exchange || 'NFO',
                                  tradingSymbol: option.tradingSymbol,
                                  strikePrice: extractStrikeFromSymbol(option.tradingSymbol),
                                  ltp: getLivePrice(option.exchange, option.instrumentId)
                                }));
                                
                                const exportData = {
                                  symbolName: symbolFilters.symbolName || 'NIFTY',
                                  expiryDate: extractedExpiryDate,
                                  ceOptions: ceOptionsWithLTP,
                                  peOptions: peOptionsWithLTP
                                };
                                
                                console.log('📊 Export data:', {
                                  symbolName: exportData.symbolName,
                                  expiryDate: exportData.expiryDate,
                                  peCount: exportData.peOptions.length
                                });
                                
                                const ceLTPSum = ceOptionsWithLTP.reduce((sum, opt) => sum + (opt.ltp || 0), 0);
                                const peLTPSum = peOptionsWithLTP.reduce((sum, opt) => sum + (opt.ltp || 0), 0);
                                setBaselineCESum(ceLTPSum);
                                setBaselinePESum(peLTPSum);
                                console.log('💰 Baseline sums stored - CE:', ceLTPSum.toFixed(2), 'PE:', peLTPSum.toFixed(2));
                                
                                const response = await api.post('/options-analysis/export', exportData);
                                
                                if (response.data.success) {
                                  const result = response.data.data;
                                  const clearedText = result.replacedCount > 0 ? `\\n(Cleared ${result.replacedCount} existing records from table)` : '';
                                  alert(`✅ Analysis exported successfully!\\n\\nSession ID: ${result.exportSessionId}\\nTotal Records: ${result.totalExported}\\nCE Options: ${result.ceCount}\\nPE Options: ${result.peCount}${clearedText}\\n\\n💡 All previous data has been replaced with current LTP values.`);
                                  console.log('✅ Export successful:', result);
                                  
                                  // Auto-collapse the filter section after successful analysis
                                  setIsFilterCollapsed(true);
                                } else {
                                  alert('❌ Export failed: ' + response.data.error);
                                }
                                
                              } catch (error) {
                                console.error('❌ Export error:', error);
                                alert('❌ Export failed: ' + error.message);
                              }
                            }}
                          >
                            📊 Analyse Options Chain
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-gray-500 py-2 text-xs">
                      Select symbol, expiry & strike to load chain info
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtered Results - Hidden - Smart builder handles this automatically */}
              {false && filteredSymbols.length > 0 && (
                <div className="mt-2 bg-white p-2 rounded border border-blue-200">
                  <h5 className="font-medium text-gray-700 mb-2 text-xs">
                    Found {filteredSymbols.length} matching symbol(s):
                  </h5>
                  <div className="grid grid-cols-1 gap-2">
                    {filteredSymbols.map((symbol, idx) => (
                      <div 
                        key={idx} 
                        className="bg-white p-2 rounded border hover:bg-blue-50 transition-colors"
                      >
                        <div className="font-medium text-blue-600 text-xs">{symbol.trading_symbol}</div>
                        <div className="text-xs text-gray-600">
                          Strike: {symbol.parsed_strike || symbol.strike_price || '—'} | Type: {symbol.parsed_option_type || symbol.option_type || '—'}
                        </div>
                        <div className="mt-1 flex space-x-2">
                          <button
                            onClick={() => handleAddToWatchlist(symbol)}
                            className="btn btn-primary text-xs px-2 py-1"
                            disabled={watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrument_id
                            )}
                          >
                            {watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrument_id
                            ) ? 'Added' : 'Add to Watch'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* Search Results for Active Exchange (Hide for NFO) */}
        {activeTab !== 'NFO' && (
          <div className="card">
            {/* Search Results */}
            {exchangeSearchResults[activeTab] && exchangeSearchResults[activeTab].length > 0 && (
              <div className="mt-4 max-h-64 overflow-y-auto rounded-lg shadow-sm border border-gray-200">
                <table className="table-auto w-full text-sm">
                  <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Symbol</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Instrument</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchangeSearchResults[activeTab].map((symbol, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-4 py-3 font-semibold text-gray-800">{symbol.tradingSymbol}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{symbol.instrumentType}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleAddToWatchlist(symbol)}
                            className="btn btn-primary text-xs px-2 py-1"
                            disabled={watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrumentId
                            )}
                          >
                            {watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrumentId
                            ) ? 'Added' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Watchlist Tables */}
        {(activeTab === 'NFO' || activeTab === 'BFO') ? (
          // Split view for NFO/BFO: CE on left, PE on right
          <div className="space-y-4">
            {/* Options Tables and Quick Order Entry Layout */}
            <div className="flex gap-3 items-start">
              {/* Collapsible Options Tables Section (CE, PE only) */}
              {!isOptionsTableCollapsed ? (
                <>
                  <div className="flex-none" style={{width: '12%'}}>
                    {renderWatchlistTable(ceOptions, '📈 Call Options', 'CE')}
                  </div>
                  <div className="flex-none flex items-start" style={{width: '12%'}}>
                    <div className="flex-1">
                      {renderWatchlistTable(peOptions, '📉 Put Options', 'PE')}
                    </div>
                    {/* Collapse Button positioned next to Put Options table */}
                    <button
                      onClick={() => {
                        setIsOptionsTableCollapsed(true);
                        localStorage.setItem('advancedTrading_optionsTableCollapsed', 'true');
                      }}
                      className="flex-none bg-blue-600 text-white px-1 py-1 rounded-r text-xs shadow hover:bg-blue-700 transition-colors ml-[-2px] mt-[50%]"
                      style={{transform: 'translateY(-50%)'}}
                      title="Collapse Call & Put Options"
                    >
                      ◀
                    </button>
                  </div>
                </>
              ) : (
                /* Expand Button when collapsed */
          <button
              onClick={() => {
                setIsOptionsTableCollapsed(false);
                localStorage.setItem('advancedTrading_optionsTableCollapsed', 'false');
              }}
              className="
                bg-green-300 
                text-white 
                w-8 
                h-72 
                rounded-lg 
                shadow-lg 
                hover:bg-green-500 
                transition-colors
                flex 
                items-center 
                justify-center 
                self-center
              "
              style={{ display: "flex" }}
              title="Expand Call & Put Options"
            >
              <span className="-rotate-90 whitespace-nowrap text-[15px] text-center">
                {selectedChainInfo.symbolName} CE - PE Strike Price Details ▶
              </span>
            </button>


                
              )}

              {/* Volume Update Table (Always Visible) */}
              <div className="flex-none" style={{width: '18%'}}>
                    <div className="bg-white rounded-lg shadow-sm border">
                        {ceOptions.length > 0 && nextExpiryDate ? (
                          <div>
                            <table className="w-full table-fixed">
                              <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                                <tr className="bg-purple-50">
                                  <th colSpan="4" className="text-center" style={{height: '32px', padding: '0'}}>
                                    <div className="flex h-full">
                                      <button
                                        onClick={handleExportBullBear}
                                        disabled={isExporting || ceOptions.length === 0}
                                        className={`flex-1 h-full px-4 text-sm font-semibold transition-colors ${
                                          isExporting || ceOptions.length === 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg rounded-t-lg'
                                        }`}
                                        title="Click to Update"
                                      >
                                        {isExporting ? '⏳ Exporting...' : '📊 Volume Update'}
                                      </button>
                                    </div>
                                  </th>
                                </tr>
                                <tr className="text-[11px] font-semibold text-gray-700 h-[28px]">
                                  <th className="text-center py-0.5 px-2 w-1/2 border-r border-gray-200" colSpan="2">🐂 Bull</th>
                                  <th className="text-center py-0.5 px-2 w-1/2" colSpan="2">🐻 Bear</th>
                                </tr>
                              </thead>
                              <tbody>
                            {peOptions.map((peItem, idx) => {
                              // Get corresponding CE item for this row
                              const ceItem = ceOptions[idx];
                              
                              // Extract strike prices - support both NFO and BFO formats
                              let peStrike = null, ceStrike = null;
                              
                              // For PE strike extraction
                              if (peItem.parsed_strike) {
                                peStrike = peItem.parsed_strike.toString();
                              } else {
                                // Fallback: try to parse from trading symbol
                                let match = peItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
                                if (match) {
                                  peStrike = match[1];
                                } else {
                                  match = peItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
                                  if (match) {
                                    peStrike = match[1];
                                  }
                                }
                              }
                              
                              // For CE strike extraction
                              if (ceItem) {
                                if (ceItem.parsed_strike) {
                                  ceStrike = ceItem.parsed_strike.toString();
                                } else {
                                  // Fallback: try to parse from trading symbol
                                  let match = ceItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
                                  if (match) {
                                    ceStrike = match[1];
                                  } else {
                                    match = ceItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
                                    if (match) {
                                      ceStrike = match[1];
                                    }
                                  }
                                }
                              }

                              if (!peStrike && !ceStrike) return null;

                              // Find next expiry symbols for PE strike
                              const nextPE = peStrike ? nextExpirySymbols.find(s => s.strike === peStrike && s.type === 'PE') : null;
                              
                              // Find next expiry symbols for CE strike
                              const nextCE = ceStrike ? nextExpirySymbols.find(s => s.strike === ceStrike && s.type === 'CE') : null;

                              // Get depth data
                              const ceKey = nextCE ? `${nextCE.exchange}|${nextCE.token}` : null;
                              const peKey = nextPE ? `${nextPE.exchange}|${nextPE.token}` : null;

                              // Debug logging for first row only
                              if (idx === 0) {
                                console.log('🔍 Debug Bulls vs Bears (first row):', {
                                  peStrike,
                                  ceStrike,
                                  nextCE,
                                  nextPE,
                                  ceKey,
                                  peKey,
                                  ceDepthData: ceKey ? depthData[ceKey] : null,
                                  peDepthData: peKey ? depthData[peKey] : null
                                });
                              }

                              // Get live WebSocket data from respective strikes
                              // Column 1: CE TBQ from Call Options strike
                              // Column 2: PE TSQ from Put Options strike  
                              // Column 3: PE TBQ from Put Options strike
                              // Column 4: CE TSQ from Call Options strike
                              const liveCeTBQ = (ceKey && depthData[ceKey]?.tbq);
                              const liveCeTSQ = (ceKey && depthData[ceKey]?.tsq);
                              const livePeTBQ = (peKey && depthData[peKey]?.tbq);
                              const livePeTSQ = (peKey && depthData[peKey]?.tsq);
                              
                              // Calculate difference if in flip mode
                              let displayCeTBQ, displayCeTSQ, displayPeTBQ, displayPeTSQ;
                              
                              if (isFlipped) {
                                if (exportedData.length > 0) {
                                  // Find exported data for CE and PE strikes
                                  const exportedCERow = ceStrike ? exportedData.find(row => row.strike_price === parseInt(ceStrike)) : null;
                                  const exportedPERow = peStrike ? exportedData.find(row => row.strike_price === parseInt(peStrike)) : null;
                                  
                                  // Calculate difference: (live - exported) / 400
                                  if (exportedCERow) {
                                    const ceTBQDiff = (typeof liveCeTBQ === 'number' ? liveCeTBQ : 0) - (exportedCERow.ce_tbq || 0);
                                    const ceTSQDiff = (typeof liveCeTSQ === 'number' ? liveCeTSQ : 0) - (exportedCERow.ce_tsq || 0);
                                    displayCeTBQ = Math.round(ceTBQDiff / 400);
                                    displayCeTSQ = Math.round(ceTSQDiff / 400);
                                  } else {
                                    displayCeTBQ = displayCeTSQ = '-';
                                  }
                                  
                                  if (exportedPERow) {
                                    const peTBQDiff = (typeof livePeTBQ === 'number' ? livePeTBQ : 0) - (exportedPERow.pe_tbq || 0);
                                    const peTSQDiff = (typeof livePeTSQ === 'number' ? livePeTSQ : 0) - (exportedPERow.pe_tsq || 0);
                                    displayPeTBQ = Math.round(peTBQDiff / 400);
                                    displayPeTSQ = Math.round(peTSQDiff / 400);
                                  } else {
                                    displayPeTBQ = displayPeTSQ = '-';
                                  }
                                } else {
                                  // No exported data yet - show placeholders instead of live depth
                                  displayCeTBQ = displayCeTSQ = displayPeTBQ = displayPeTSQ = '-';
                                }
                              } else {
                                // Live view - show actual WebSocket data
                                displayCeTBQ = typeof liveCeTBQ === 'number' ? liveCeTBQ : '-';
                                displayCeTSQ = typeof liveCeTSQ === 'number' ? liveCeTSQ : '-';
                                displayPeTBQ = typeof livePeTBQ === 'number' ? livePeTBQ : '-';
                                displayPeTSQ = typeof livePeTSQ === 'number' ? livePeTSQ : '-';
                              }
                              
                              // Helper function to get color class based on value and flip mode
                              const getColorClass = (value, baseColor) => {
                                if (isFlipped && typeof value === 'number') {
                                  // In difference mode, color based on positive/negative
                                  return value > 0 ? 'text-blue-600' : value < 0 ? 'text-red-600' : 'text-gray-600';
                                }
                                // In live mode, use base color
                                return baseColor;
                              };

                              return (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-150 h-[28px]">
                                  <td className={`text-center py-0.5 px-2 font-semibold text-xs ${getColorClass(displayCeTBQ, 'text-blue-600')}`}>{displayCeTBQ}</td>
                                  <td className={`text-center py-0.5 px-2 font-semibold text-xs border-r-[5px] border-gray-150 ${getColorClass(displayPeTSQ, 'text-red-600')}`}>{displayPeTSQ}</td>
                                  <td className={`text-center py-0.5 px-2 font-semibold text-xs ${getColorClass(displayPeTBQ, 'text-blue-600')}`}>{displayPeTBQ}</td>
                                  <td className={`text-center py-0.5 px-2 font-semibold text-xs ${getColorClass(displayCeTSQ, 'text-red-600')}`}>{displayCeTSQ}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-xs">
                          {!nextExpiryDate ? 'Loading Volumes...' : 'No depth data available'}
                        </p>
                        {nextExpiryDate && (
                          <p className="text-gray-400 text-xs mt-1">
                            Symbols: {nextExpirySymbols.length} | Depth: {Object.keys(depthData).length}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              
              {/* Advanced Quick Order Entry Panel */}
              <div className="flex-1 space-y-4">
                {/* Modern Order Entry Panel */}
                <div className={`${uiStyles.modernCard} overflow-hidden`}>
                  <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 text-white">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">Quick Order Entry</h3>
                          <p className="text-white/80 text-sm">Lightning-fast order execution</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedCEStrike(null);
                          setSelectedPEStrike(null);
                          setCEQty('');
                          setPEQty('');
                        }}
                        className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-white font-semibold hover:bg-white/30 transition-all duration-300 shadow-lg"
                        title="Clear all selections"
                      >
                        <span className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Clear</span>
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                    {/* Calculate MTM values */}
                    {(() => {
                      // Current live LTP sums
                      const currentCESum = ceOptions.reduce((sum, option) => {
                        const ltp = getLivePrice(option.exchange, option.instrumentId);
                        return sum + (ltp || 0);
                      }, 0);
                      
                      const currentPESum = peOptions.reduce((sum, option) => {
                        const ltp = getLivePrice(option.exchange, option.instrumentId);
                        return sum + (ltp || 0);
                      }, 0);
                      
                      // Calculate MTM (difference from baseline)
                      const ceMTM = currentCESum - baselineCESum;
                      const peMTM = currentPESum - baselinePESum;
                      
                      return (
                        <>
                          {/* Modern Order Entry Table */}
                          <div className={`${uiStyles.modernTable} overflow-hidden`}>
                            <div className="bg-gradient-to-r from-slate-800 via-gray-900 to-slate-800 text-white">
                              <div className="grid grid-cols-9 gap-2 p-4 text-sm font-bold text-center">
                                <div className="flex items-center justify-center space-x-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                  </svg>
                                  <span>LTP</span>
                                </div>
                                <div>Qty</div>
                                <div className="text-green-400">Buy</div>
                                <div className="text-blue-400">Stk Low</div>
                                <div className="text-orange-400">MTM</div>
                                <div className="text-red-400">Stk High</div>
                                <div className="text-red-400">Sell</div>
                                <div>Re Enter</div>
                              </div>
                            </div>
                            
                            {/* Modern Table Body */}
                            <div className="bg-gradient-to-br from-white to-gray-50 divide-y divide-gray-200">
                              {/* CE Row */}
                              <div className="grid grid-cols-9 gap-1 p-2 items-center hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-300">
                                {/* LTP */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-2 rounded-lg font-bold shadow-md">
                                    <div className="text-sm">
                                      {selectedCEStrike ? (
                                        getLivePrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs opacity-80">CE LTP</div>
                                  </div>
                                </div>

                                {/* Qty */}
                                <div className="text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => adjustQuantity('CE', 'decrease')}
                                      disabled={!selectedCEStrike || isDecreaseDisabled('CE')}
                                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white w-6 h-6 rounded-lg text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-md transition-all duration-300 flex items-center justify-center"
                                      title={`Decrease quantity by ${selectedCEStrike?.lotSize || 25}`}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                      </svg>
                                    </button>
                                    <input
                                      type="text"
                                      value={ceQty}
                                      onChange={(e) => setCEQty(e.target.value)}
                                      className="w-12 px-1 py-1 border border-gray-200 rounded-lg text-center text-xs font-bold bg-white focus:border-blue-500 transition-all duration-300"
                                      disabled={!selectedCEStrike}
                                      placeholder={selectedCEStrike ? (selectedCEStrike.lotSize || '25') : ''}
                                      title={selectedCEStrike ? `Lot Size: ${selectedCEStrike.lotSize || 25}` : ''}
                                    />
                                    <button
                                      onClick={() => adjustQuantity('CE', 'increase')}
                                      disabled={!selectedCEStrike}
                                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white w-6 h-6 rounded-lg text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-md transition-all duration-300 flex items-center justify-center"
                                      title={`Increase quantity by ${selectedCEStrike?.lotSize || 25}`}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>

                                {/* Buy */}
                                <div className="text-center">
                                  <button
                                    onClick={() => handleQuickOrder(selectedCEStrike, 'BUY', 'CE')}
                                    disabled={!selectedCEStrike}
                                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-300"
                                  >
                                    Buy
                                  </button>
                                </div>

                                {/* Stk Low */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-1.5 rounded-lg">
                                    <div className="text-sm font-bold text-blue-700">
                                      {selectedCEStrike ? (
                                        getLowPrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs text-blue-600">Low</div>
                                  </div>
                                </div>

                                {/* MTM */}
                                <div className="text-center">
                                  <div className={`p-1.5 rounded-lg ${ceMTM >= 0 ? 'bg-gradient-to-br from-green-100 to-green-200' : 'bg-gradient-to-br from-red-100 to-red-200'}`}>
                                    <div className={`text-sm font-bold ${ceMTM >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {baselineCESum > 0 ? (
                                        <>
                                          <div>{ceMTM >= 0 ? '+' : '-'}{Math.abs(ceMTM / 10).toFixed(0)}</div>
                                        </>
                                      ) : (
                                        <div className="text-gray-400">-</div>
                                      )}
                                    </div>
                                    <div className={`text-xs ${ceMTM >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      MTM
                                    </div>
                                  </div>
                                </div>

                                {/* Stk High */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-1.5 rounded-lg">
                                    <div className="text-sm font-bold text-red-700">
                                      {selectedCEStrike ? (
                                        getHighPrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs text-red-600">High</div>
                                  </div>
                                </div>

                                {/* Sell */}
                                <div className="text-center">
                                  <button
                                    onClick={() => handleQuickOrder(selectedCEStrike, 'SELL', 'CE')}
                                    disabled={!selectedCEStrike}
                                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-300"
                                  >
                                    Sell
                                  </button>
                                </div>

                                {/* Re Enter */}
                                <div className="text-center">
                                  <div className="text-sm font-bold text-blue-600">
                                    {selectedCEStrike ? `${selectedCEStrike.strike}CE` : '-'}
                                  </div>
                                  <div className="text-xs text-gray-500">Strike</div>
                                </div>
                              </div>
                              
                              {/* PE Row */}
                              <div className="grid grid-cols-9 gap-1 p-2 items-center hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-300">
                                {/* LTP */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-2 rounded-lg font-bold shadow-md">
                                    <div className="text-sm">
                                      {selectedPEStrike ? (
                                        getLivePrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs opacity-80">PE LTP</div>
                                  </div>
                                </div>

                                {/* Qty */}
                                <div className="text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => adjustQuantity('PE', 'decrease')}
                                      disabled={!selectedPEStrike || isDecreaseDisabled('PE')}
                                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white w-6 h-6 rounded-lg text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-md transition-all duration-300 flex items-center justify-center"
                                      title={`Decrease quantity by ${selectedPEStrike?.lotSize || 25}`}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                      </svg>
                                    </button>
                                    <input
                                      type="text"
                                      value={peQty}
                                      onChange={(e) => setPEQty(e.target.value)}
                                      className="w-12 px-1 py-1 border border-gray-200 rounded-lg text-center text-xs font-bold bg-white focus:border-purple-500 transition-all duration-300"
                                      disabled={!selectedPEStrike}
                                      placeholder={selectedPEStrike ? (selectedPEStrike.lotSize || '25') : ''}
                                      title={selectedPEStrike ? `Lot Size: ${selectedPEStrike.lotSize || 25}` : ''}
                                    />
                                    <button
                                      onClick={() => adjustQuantity('PE', 'increase')}
                                      disabled={!selectedPEStrike}
                                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white w-6 h-6 rounded-lg text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-md transition-all duration-300 flex items-center justify-center"
                                      title={`Increase quantity by ${selectedPEStrike?.lotSize || 25}`}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>

                                {/* Buy */}
                                <div className="text-center">
                                  <button
                                    onClick={() => handleQuickOrder(selectedPEStrike, 'BUY', 'PE')}
                                    disabled={!selectedPEStrike}
                                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-300"
                                  >
                                    Buy
                                  </button>
                                </div>

                                {/* Stk Low */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-1.5 rounded-lg">
                                    <div className="text-sm font-bold text-blue-700">
                                      {selectedPEStrike ? (
                                        getLowPrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs text-blue-600">Low</div>
                                  </div>
                                </div>

                                {/* MTM */}
                                <div className="text-center">
                                  <div className={`p-1.5 rounded-lg ${peMTM >= 0 ? 'bg-gradient-to-br from-green-100 to-green-200' : 'bg-gradient-to-br from-red-100 to-red-200'}`}>
                                    <div className={`text-sm font-bold ${peMTM >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {baselinePESum > 0 ? (
                                        <>
                                          <div>{peMTM >= 0 ? '+' : '-'}{Math.abs(peMTM / 10).toFixed(0)}</div>
                                        </>
                                      ) : (
                                        <div className="text-gray-400">-</div>
                                      )}
                                    </div>
                                    <div className={`text-xs ${peMTM >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      MTM
                                    </div>
                                  </div>
                                </div>

                                {/* Stk High */}
                                <div className="text-center">
                                  <div className="bg-gradient-to-br from-red-100 to-red-200 p-1.5 rounded-lg">
                                    <div className="text-sm font-bold text-red-700">
                                      {selectedPEStrike ? (
                                        getHighPrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                      ) : '-'}
                                    </div>
                                    <div className="text-xs text-red-600">High</div>
                                  </div>
                                </div>

                                {/* Sell */}
                                <div className="text-center">
                                  <button
                                    onClick={() => handleQuickOrder(selectedPEStrike, 'SELL', 'PE')}
                                    disabled={!selectedPEStrike}
                                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-300"
                                  >
                                    Sell
                                  </button>
                                </div>

                                {/* Re Enter */}
                                <div className="text-center">
                                  <div className="text-sm font-bold text-red-600">
                                    {selectedPEStrike ? `${selectedPEStrike.strike}PE` : '-'}
                                  </div>
                                  <div className="text-xs text-gray-500">Strike</div>
                                </div>
                              </div>

                              {/* Modern Order Type Controls */}
                              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-2 border-t border-gray-200">
                                <div className="grid grid-cols-9 gap-1 items-center">
                                  <div className="col-span-2"></div>
                                  
                                  {/* BUY SIDE CONTROLS */}
                                  <div className="text-center">
                                    <div className="flex justify-center items-center gap-1">
                                      <button
                                        onClick={() => setOrderType('LIMIT')}
                                        className={`px-2 py-1 rounded-lg text-xs font-bold transition-all duration-300 shadow-sm ${
                                          orderType === 'LIMIT'
                                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                                        }`}
                                      >
                                        LMT
                                      </button>
                                      <button
                                        onClick={() => setOrderType('MARKET')}
                                        className={`px-2 py-1 rounded-lg text-xs font-bold transition-all duration-300 shadow-sm ${
                                          orderType === 'MARKET'
                                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                                        }`}
                                      >
                                        MKT
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="col-span-3"></div>

                                  {/* SELL SIDE CONTROLS */}
                                  <div className="text-center">
                                    <div className="flex justify-center items-center gap-1">
                                      <button
                                        onClick={() => setProductType('NRML')}
                                        className={`px-2 py-1 rounded-lg text-xs font-bold transition-all duration-300 shadow-sm ${
                                          productType === 'NRML'
                                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105'
                                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                                        }`}
                                      >
                                        NML
                                      </button>
                                      <button
                                        onClick={() => setProductType('MIS')}
                                        className={`px-2 py-1 rounded-lg text-xs font-bold transition-all duration-300 shadow-sm ${
                                          productType === 'MIS'
                                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105'
                                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                                        }`}
                                      >
                                        MIS
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="col-span-2"></div>
                                </div>
                              </div>
                            </div>
                          </div>                                  
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Modern Positions Panel */}
                <div className={`${uiStyles.modernCard} mt-6 overflow-hidden`}>
                  <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-6 py-4 text-white">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">Live Positions</h3>
                          <p className="text-white/80 text-sm">Real-time portfolio tracking</p>
                        </div>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                        <span className="text-lg font-bold">MTM: {calculateTotalMTM()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto bg-gradient-to-br from-white to-gray-50">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                        <tr className="font-semibold text-gray-700 text-sm h-[28px]">
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-left">Instrument</th>
                          <th className="p-2 text-center">Qty</th>
                          <th className="p-2 text-center">BAvg</th>
                          <th className="p-2 text-center">SAvg</th>
                          <th className="p-2 text-center">LTP</th>
                          <th className="p-2 text-center">DayP&L</th>
                          <th className="p-2 text-center">NetP&L</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {positions.length > 0 ? (
                          positions.map((pos, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors h-[28px]">
                              <td className="p-2 text-left text-gray-700 font-medium">{pos.type}</td>
                              <td className="p-2 text-left">
                                <div className="font-semibold text-gray-800">{pos.instrument}</div>
                              </td>
                              <td className="p-2 text-center font-semibold">{pos.qty}</td>
                              <td className="p-2 text-center">{pos.buyAvg}</td>
                              <td className="p-2 text-center">{pos.sellAvg}</td>
                              <td className="p-2 text-center font-semibold">{pos.ltp}</td>
                              <td className={`p-2 text-center font-bold ${parseFloat(pos.dayPnl) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {parseFloat(pos.dayPnl) >= 0 ? '+' : ''}{pos.dayPnl}
                              </td>
                              <td className={`p-2 text-center font-bold ${parseFloat(pos.netPnl) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {parseFloat(pos.netPnl) >= 0 ? '+' : ''}{pos.netPnl}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="p-4 text-center text-gray-500">
                              No positions yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Regular table for other exchanges
          filteredWatchlist.length > 0 ? (
            <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
              <table className="table-auto w-full">
                <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold">Symbol</th>
                    <th className="py-3 px-4 text-left font-semibold">Exchange</th>
                    <th className="py-3 px-4 text-left font-semibold">LTP</th>
                    <th className="py-3 px-4 text-left font-semibold">Change %</th>
                    <th className="py-3 px-4 text-left font-semibold">Volume</th>
                    <th className="py-3 px-4 text-left font-semibold">High</th>
                    <th className="py-3 px-4 text-left font-semibold">Low</th>
                    <th className="py-3 px-4 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWatchlist.map((item, idx) => {
                    const ltp = getLivePrice(item.exchange, item.instrumentId);
                    const change = getPriceChange(item.exchange, item.instrumentId);
                    const priceKey = `${item.exchange}|${item.instrumentId}`;
                    const priceData = livePrices[priceKey];
                    
                    return (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors border-b border-gray-100">
                        <td className="font-semibold py-3 px-4 text-gray-800">{item.tradingSymbol}</td>
                        <td className="py-3 px-4">
                          <span className="px-2.5 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md text-xs font-semibold">
                            {item.exchange}
                          </span>
                        </td>
                        <td className={`text-base font-bold py-3 px-4 ${ltp !== null ? 'text-blue-600' : 'text-gray-400'}`}>
                          {ltp !== null ? `${ltp.toFixed(2)}` : '—'}
                        </td>
                        <td className={change !== null && parseFloat(change) >= 0 ? 'text-success font-semibold' : change !== null ? 'text-danger font-semibold' : 'text-gray-400'}>
                          {change !== null ? `${parseFloat(change) > 0 ? '+' : ''}${parseFloat(change).toFixed(2)}%` : '—'}
                        </td>
                        <td className="text-gray-600">
                          {priceData?.v ? parseInt(priceData.v).toLocaleString() : '—'}
                        </td>
                        <td className="text-green-600 font-medium">
                          {priceData?.h && priceData.h > 0 ? `${parseFloat(priceData.h).toFixed(2)}` : '—'}
                        </td>
                        <td className="text-red-600 font-medium">
                          {priceData?.l && priceData.l > 0 ? `${parseFloat(priceData.l).toFixed(2)}` : '—'}
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleBuySell(item, 'BUY')}
                              className="btn btn-success text-sm px-3 py-1"
                            >
                              Buy
                            </button>
                            <button
                              onClick={() => handleBuySell(item, 'SELL')}
                              className="btn btn-danger text-sm px-3 py-1"
                            >
                              Sell
                            </button>
                            <button
                              onClick={() => handleRemoveFromWatchlist(item)}
                              className="btn btn-secondary text-sm px-3 py-1"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              {watchlist.length === 0 ? (
                <p className="text-gray-500">
                  No symbols in your trading view. Search and add symbols to start trading.
                </p>
              ) : (
                <p className="text-gray-500">
                  No symbols in {activeTab} exchange. Switch to another tab or add {activeTab} symbols.
                </p>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdvancedTrading;
